import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { FitText } from './FitText';
import { createPortal } from 'react-dom';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import type { Lesson, LessonDrillRef, OfferingType } from '../types/lesson';
import type { Drill } from '../types/lesson';
import { VideoEditorPlayer, type VideoEditorPlayerRef } from './VideoEditorPlayer';
import { Timeline } from './Timeline';
import type { NotableSegment, VoiceoverNotableSegmentClip } from '../types/videoEdit';
import { ClipEditorModal, type ClipSource } from './ClipEditorModal';
import { SegmentCaptureModal } from './SegmentCaptureModal';
import { useScrubPause } from '../hooks/useScrubPause';
import {
  EBTN_PRIMARY,
  EBTN_GHOST,
  EBTN_ICON,
  EBTN_SECONDARY,
  EBTN_DESTRUCTIVE,
  EBTN_ICON_ACTIVE,
} from './videoEditor/editorButtonStyles';
import { LessonOrganizerPanel } from './LessonOrganizerPanel';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import { syncSegmentsToPlayerLibrary } from '../services/playerLibrary';
import { AddComparisonSourceModal } from './AddComparisonSourceModal';
import ProgressReportModal from './ProgressReportModal';
import { EditorDebugOverlay } from './videoEditor/EditorDebugOverlay';
import { EditorErrorBoundary } from './videoEditor/EditorErrorBoundary';
import { decideAutosave, isEmptyPlan, savesAllowed } from '../services/draftGuard';
import { clearPending, readPending, savePending, shouldApplyPending } from '../services/pendingDraftStore';
import { EmptyDraftOverwriteError } from '../services/videoEditDraft';
import { EditorVideo } from './videoEditor/EditorVideo';
import { PhoneLessonPreviewBody } from './videoEditor/PhoneLessonPreview';
import { releaseCapture } from './videoEditor/captureSession';
import { placeOnMount, type Box } from './videoEditor/menuPlacement';
import { RecorderAlert } from './videoEditor/RecorderControls';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';
import { useKeyboardSafe } from './videoEditor/useKeyboardSafe';
import { usePinchZoom } from './videoEditor/usePinchZoom';
import { ZoomResetChip } from './videoEditor/ZoomResetChip';
import { viewingLayout, railWidth, landscapeSheetWidth, previewStage, PHONE_HEADER, detectPhone } from './videoEditor/phoneLayout';
import { useHeldFlag } from './videoEditor/useHeldFlag';
import { nextPartName } from './videoEditor/segmentNames';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Draft data that can be saved/loaded.
 * Legacy fields (recordingSegments, annotationSnapshots, audioRecordings, clipMasks)
 * are intentionally omitted — the new model uses only voiceover clip sequences.
 */
export interface VideoEditorDraftData {
  notableSegments: NotableSegment[];
  voiceoverNotableSegmentClips: VoiceoverNotableSegmentClip[];
  notes: string;
  /** UI preference: mute original video while scrubbing. NOT sent to cloud-run. */
  audioMuted: boolean;
  /** Drill snapshots the coach has attached to this lesson */
  attachedDrills?: LessonDrillRef[];
  /** Private coach-only notes — never displayed in player-facing views */
  coachNotes?: string;
  /** Clockwise rotation applied to the player video (0 | 90 | 180 | 270). Default: 0. */
  videoRotation?: number;
  /**
   * Transient, never stored: set on a save when this editor session loaded a
   * non-empty plan, so an empty plan written now is the coach's own deletion
   * and may replace the stored one (draftGuard.refuseEmptyOverwrite).
   */
  allowEmptyOverwrite?: boolean;
  /**
   * Transient, never stored: on a loaded draft, when it was last written
   * (VideoEditDraft.lastEditedAt). A local copy of unconfirmed changes is put
   * back on open only when it is newer (pendingDraftStore.ts).
   */
  storedLastEditedAt?: string;
}

interface LessonVideoEditorProps {
  lessonRequest?: Lesson;
  videoUrl?: string;

  title?: string;
  /**
   * Explicit second line. No production caller passes one: the auto-generated
   * "Lesson #xxxxxxxx" that used to fill it is gone, since a truncated document
   * id told the coach nothing. Kept because two dev harnesses set it, and it is
   * the admin page among them.
   */
  subtitle?: string;

  onCancel: () => void;
  /** Auto-called on changes. Should upload blobs and return data with storage paths merged in. */
  onSaveDraft?: (data: VideoEditorDraftData) => Promise<VideoEditorDraftData>;
  /** Called when user clicks "Submit lesson" */
  onSubmitDraft?: (data: VideoEditorDraftData) => Promise<void>;

  initialDraft?: VideoEditorDraftData;
  /**
   * False while the host is still reading the stored draft. Nothing is
   * written (auto-save, close, page hide) until it is true, so an editor that
   * opened before its draft arrived cannot save an empty plan over it.
   * Defaults to true for hosts that pass initialDraft synchronously.
   */
  draftReady?: boolean;

  /** Player ID — when set, enables "Save to player library" on timeline segments */
  playerId?: string;
  /** Lesson ID — recorded on saved segments for cross-lesson traceability */
  lessonId?: string;
  /** Original video ID — stored on saved segments to reconstruct the video URL later */
  originalVideoId?: string;
  /** Offering type — drives editor copy (title + organizer length hint). Optional; omitting falls back to "Record Lesson" / ~16min defaults. */
  offeringType?: OfferingType;
  /** Fires after a successful submission, after a brief success-state flash. Use to navigate, toast, etc. */
  onSubmitSuccess?: (lessonId: string, playerName?: string) => void;
  /** True when this lesson is the final program lesson (offering=training_program AND lessonNumber===totalLessons).
   *  When true: the submit button is relabeled, opens the ProgressReportModal instead of the standard submit
   *  confirmation, and the organizer panel renders the priming pill + microcopy. */
  isFinalProgramLesson?: boolean;
  /** Player first name — used in the ProgressReportModal headline + question personalization +
   *  organizer-panel microcopy. Falls back to "player" inside the modal when omitted. */
  playerFirstName?: string;
  /** Total program lessons — drives the modal's journey strip dot count. Defaults to 4 when omitted. */
  totalLessons?: number;
  /** Player's full display name, resolved by the host. Used for the header title
   *  and the details popover. Omitting it falls the title back to the lesson type. */
  playerName?: string;
  /** Player's photo, resolved by the host from playerProfile then the order. */
  playerAvatarUrl?: string;
  /** Player's UTR, if they have one. */
  playerUtr?: number;
  /** Player's stated goal (from playerProfile.goal). Surfaced as a small reference card
   *  at the top of the ProgressReportModal so the coach can write toward it. Optional. */
  playerGoal?: string;
  /**
   * Final-lesson submit handler. Owns the lesson submit + report write
   * pipeline in the parent (lesson submit first, then report). Throws
   * ONLY on lesson-side failure so the modal can stay open for retry.
   * Resolves on lesson success regardless of report success — parent
   * handles the partial-failure toast.
   */
  onFinalSubmit?: (params: {
    draftData: VideoEditorDraftData;
    report: { whatImproved: string; keepDoing: string; nextFocus: string; personalNote: string };
  }) => Promise<void>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

// Number of editors currently mounted. A counter rather than a boolean so a
// second editor mounting before the first unmounts (a remount during a route
// change, say) cannot clear the flag early — the attribute is removed only
// when the last one goes. Module scope: shared by every instance.
let openEditorCount = 0;
/** Spec 14.10: the bars hint has shown during this page's life. */
let barsHintShownThisPage = false;

const AUTOSAVE_DEBOUNCE_MS = 1500;
/** A failed save is tried again after 2s, then 4s, 8s and so on, up to every 30s. */
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 30_000;
/** A save not confirmed after this long is shown as not saved (the SDKs keep retrying it). */
const SAVE_STALL_MS = 45_000;
/** How long closing waits for the last save before it lets the coach go; the save carries on. */
const CLOSE_WAIT_MS = 3000;

/**
 * A fingerprint of a draft, used only to answer "is what is on screen the same
 * as what was last written?".
 *
 * Key order is normalised because clips make a round trip through Firestore and
 * come back in whatever order it chose. Without sorting, an unchanged draft
 * would fingerprint differently after every save, schedule another one, and
 * loop.
 *
 * Blobs are reduced to size and type: JSON.stringify turns a Blob into `{}`, so
 * two different recordings would otherwise look identical. Their clip ids
 * already differ, so this is belt and braces rather than the only guard.
 *
 * undefined drops out, which is what we want — Firestore does not store it, so
 * an absent field and an explicit undefined are the same write.
 */
function draftKey(d: VideoEditorDraftData): string {
  return JSON.stringify(d, (_k, v) => {
    if (v instanceof Blob) return `blob:${v.size}:${v.type}`;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        out[k] = (v as Record<string, unknown>)[k];
      }
      return out;
    }
    return v;
  });
}

/**
 * The editor inside its own error boundary: a render error shows the error
 * text and the build id rather than a blank page.
 */
export function LessonVideoEditor(props: LessonVideoEditorProps) {
  return (
    <EditorErrorBoundary variant="editor">
      <LessonVideoEditorInner {...props} />
    </EditorErrorBoundary>
  );
}

function LessonVideoEditorInner({
  lessonRequest,
  videoUrl: directVideoUrl,
  title,
  subtitle,
  onCancel,
  onSaveDraft,
  onSubmitDraft,
  initialDraft,
  playerId,
  lessonId,
  originalVideoId,
  offeringType,
  onSubmitSuccess,
  isFinalProgramLesson = false,
  playerFirstName,
  playerName,
  playerAvatarUrl,
  playerUtr,
  totalLessons,
  playerGoal,
  onFinalSubmit,
  draftReady = true,
}: LessonVideoEditorProps) {
  const videoUrl = directVideoUrl || lessonRequest?.playerVideoUrl || '';
  /**
   * The lesson type on its own, reused for the title and as the fallback when
   * the host has no name for the player yet.
   */
  const typeLabel = offeringType === 'shot_consult' ? 'Shot Consult' : 'Lesson';
  /** First name only. The title is a sentence, not a record. */
  const titleFirstName = (playerFirstName || playerName || '').trim().split(' ')[0];
  const displayTitle = title || typeLabel;

  const videoPlayerRef = useRef<VideoEditorPlayerRef>(null);
  /** The controls bar, which the capture panel docks against on a phone. */
  const [transportEl, setTransportEl] = useState<HTMLDivElement | null>(null);
  /* A phone, in either orientation (videoEditor/phoneLayout.ts). Everything
     gated on it is phone layout; desktop renders as before. */
  const phoneViewport = usePhoneLayout();
  /** The main video for the editorDebug overlay, read through the player's handle. */
  const debugVideos = useMemo(() => [{
    label: 'main',
    ref: { get current() { return videoPlayerRef.current?.getVideoElement() ?? null; } },
  }], []);
  const isMobile = phoneViewport.phone;
  /* Spec 9.6: with a text field focused on a phone, the editor fits the
     visual viewport above the keyboard and covers the page underneath. */
  const editorRootRef = useRef<HTMLDivElement>(null);
  const keyboardUp = useKeyboardSafe(editorRootRef, isMobile);
  /* Spec 9.7: pinch to zoom the viewing stage on a phone. The play controls
     and the header never zoom. Off while the recorder is over it. */
  const viewStageRef = useRef<HTMLDivElement>(null);
  const viewZoomRef = useRef<HTMLDivElement>(null);

  /** Player details popover, opened from the name in the title. */
  const [playerCardOpen, setPlayerCardOpen] = useState(false);
  const [playerCardAnchor, setPlayerCardAnchor] = useState<{ x: number; y: number } | null>(null);
  /* Phone (spec 14.2): the card opens where it fits, from the chip's rect. */
  const [playerCardBox, setPlayerCardBox] = useState<Box | null>(null);
  const playerChipRef = useRef<HTMLButtonElement>(null);
  const playerCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!playerCardOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (playerCardRef.current?.contains(t) || playerChipRef.current?.contains(t)) return;
      setPlayerCardOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlayerCardOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [playerCardOpen]);

  // ── Core state ───────────────────────────────────────────────────────────────
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoRotation, setVideoRotation] = useState<0 | 90 | 180 | 270>(0);

  // ── Notable segments ─────────────────────────────────────────────────────────
  const [notableSegments, setNotableSegments] = useState<NotableSegment[]>([]);

  // Naming modal
  /** Bounds of the pending capture, or null when the modal is closed. */
  const [pendingSegmentBounds, setPendingSegmentBounds] = useState<{ startTime: number; endTime: number } | null>(null);
  /**
   * Where the coach last dragged the capture panel. Editor state rather than
   * localStorage, so it holds across captures within a session and resets when
   * the editor is closed and reopened.
   */
  const [capturePanelPos, setCapturePanelPos] = useState<{ x: number; y: number } | null>(null);
  const [segmentNameInput, setSegmentNameInput] = useState('');

  // ── Voiceover clips (in sequence order) ──────────────────────────────────────
  const [voiceoverClips, setVoiceoverClips] = useState<VoiceoverNotableSegmentClip[]>([]);

  // ── Notable segment delete confirmation ───────────────────────────────────────
  const [showNotableSegDeleteConfirm, setShowNotableSegDeleteConfirm] = useState<{ segmentId: string; clipCount: number } | null>(null);

  // ── Clip editor modal ─────────────────────────────────────────────────────────
  const [clipEditorPrimarySource, setClipEditorPrimarySource] = useState<ClipSource | null>(null);
  const [clipEditorSecondarySource, setClipEditorSecondarySource] = useState<ClipSource | null>(null);
  const [showClipEditorModal, setShowClipEditorModal] = useState(false);
  const [showComparisonSourceModal, setShowComparisonSourceModal] = useState(false);

  /* Pause the lesson video when either editor modal opens.
     It used to keep playing behind them — audible, and still burning decode
     while the coach worked in a dialog that covers it.

     One effect rather than a call at each of the eight places that open these
     modals (Add Recording, Side-by-Side, the per-segment entry points, the
     comparison-source confirm path). Keying on the flags means a ninth entry
     point added later is covered without anyone remembering to.

     Deliberately no resume on close. The coach opened a dialog to do something
     else and has been looking at a different video; restarting playback from a
     position they left several minutes ago, possibly with sound, is a surprise
     rather than a convenience. Play is one click and it is their call. */
  useEffect(() => {
    if (!showClipEditorModal && !showComparisonSourceModal && !pendingSegmentBounds) return;
    // pause() is idempotent, so no need to check whether it is playing first.
    videoPlayerRef.current?.pause();
  }, [showClipEditorModal, showComparisonSourceModal, pendingSegmentBounds]);

  /* Scrub-to-pause for the organizer's transport.
     Timeline reports the drag edges; the decision and the video live here,
     which is why the pause is not inside Timeline. Same rule as every other
     scrubber in the app: resume only if it was running when the drag began,
     so scrubbing a paused video to hunt for a frame leaves it paused. */
  const { begin: beginTransportScrub, end: endTransportScrub } = useScrubPause(
    () => videoPlayerRef.current?.getVideoElement() ?? null,
  );
  /**
   * Undefined means "open on the source list".
   *
   * It used to default to 'current', which is never absent — so the picker
   * always opened on a source. Worse, inside the recorder the current-video
   * list is usually empty (its only segment is the one being recorded), and
   * the modal's resolveTab falls through from an empty 'current' to 'player'.
   * That is why the recorder's Side-by-Side landed on From Previous Lesson
   * with the other five sources unreachable.
   */
  const [comparisonDefaultTab, setComparisonDefaultTab] =
    useState<'current' | 'player' | 'local' | 'camera' | 'drill-library' | 'pro-library' | undefined>(undefined);
  const openClipEditorAfterComparisonRef = useRef(false);

  // ── Upload file ref (for side-by-side upload flow) ────────────────────────────
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadSegmentIdRef = useRef<string | null>(null);

  const [notes, setNotes] = useState('');
  const [coachNotes, setCoachNotes] = useState('');
  const [attachedDrills, setAttachedDrills] = useState<LessonDrillRef[]>([]);

  // ── Preview state ─────────────────────────────────────────────────────────────
  const [previewClipId, setPreviewClipId] = useState<string | null>(null);
  const [previewClipUrl, setPreviewClipUrl] = useState<string | null>(null);
  const [previewClipLoading, setPreviewClipLoading] = useState(false);
  const [previewLessonOpen, setPreviewLessonOpen] = useState(false);
  const [previewLessonIdx, setPreviewLessonIdx] = useState(0);
  const [previewLessonUrl, setPreviewLessonUrl] = useState<string | null>(null);
  /** The previewed clip's own shape, for the phone preview stage (spec 5). */
  const [previewAspect, setPreviewAspect] = useState<number | null>(null);
  const handlePreviewMetadata = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight) setPreviewAspect(v.videoWidth / v.videoHeight);
  }, []);

  const handleToggleDrill = useCallback((drill: Drill) => {
    setAttachedDrills((prev) => {
      const exists = prev.some((d) => d.drillId === drill.id);
      if (exists) return prev.filter((d) => d.drillId !== drill.id);
      const ref: LessonDrillRef = {
        drillId: drill.id,
        videoUrl: drill.videoUrl,
        videoStoragePath: drill.videoStoragePath,
        title: drill.title,
        description: drill.description,
        shotType: drill.shotType,
        // Omit undefined optional fields — Firestore rejects undefined values
        ...(drill.thumbnailUrl && { thumbnailUrl: drill.thumbnailUrl }),
        ...(drill.durationSeconds !== undefined && { durationSeconds: drill.durationSeconds }),
      };
      return [...prev, ref];
    });
  }, []);

  /** The document's scroll while the phone editor covers the page; null otherwise. */
  const editorPageScrollRef = useRef<number | null>(null);
  // Lock body scroll while the editor is open so the scrollbar disappears
  useEffect(() => {
    const body = document.body;
    if (!detectPhone().phone) {
      body.style.overflow = 'hidden';
      return () => { body.style.overflow = ''; };
    }
    /* Phone (spec 14.1, 14.9): the page behind the editor is hidden and
       takes no height (html[data-editor-covering] in index.css), so no
       scroll can reveal it, and the document is a little taller than the
       screen, so a swipe up scrolls it and Safari collapses its address bar
       and tab bar. No bounce or pull-to-refresh. On close the page comes back
       at the scroll position it had. (14.1 pinned the body instead, which
       kept Safari's bars up for good.) */
    const html = document.documentElement;
    const y = window.scrollY;
    html.setAttribute('data-editor-covering', '');
    window.scrollTo(0, 0);
    /* Re-read the visible viewport once the page has settled. */
    const reread = () => requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    reread();
    /* This is the one owner of the document's scroll and height for the
       whole session (spec 14.11): nothing in the recorder, the previews or
       the sheets locks or scrolls the page on a phone. */
    editorPageScrollRef.current = 0;
    const track = () => { editorPageScrollRef.current = window.scrollY; };
    window.addEventListener('scroll', track, { passive: true });
    return () => {
      window.removeEventListener('scroll', track);
      editorPageScrollRef.current = null;
      html.removeAttribute('data-editor-covering');
      window.scrollTo(0, y);
      reread();
    };
  }, []);

  /* Opening or closing the recorder, a preview or the Compare with sheet
     leaves the document where the coach swiped it (spec 14.11): were
     anything to move it, it goes straight back, so Safari's bars stay as
     they were. */
  useEffect(() => {
    const y = editorPageScrollRef.current;
    if (y === null) return;
    const raf = requestAnimationFrame(() => {
      if (window.scrollY !== y) window.scrollTo(0, y);
    });
    return () => cancelAnimationFrame(raf);
  }, [showClipEditorModal, showComparisonSourceModal, previewLessonOpen, previewClipId, pendingSegmentBounds]);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  /** Mirrors organizerOpen for the effect below, which must not re-run on it. */
  const organizerOpenRef = useRef(false);
  /** The rail's state before the clip editor took the width, or null when closed. */
  const organizerBeforeClipRef = useRef<boolean | null>(null);

  /* Open on every width. It used to start closed below md, because the video
     took the whole column and an open plan squeezed it to 150px. On a phone the
     video is now sized to its own shape, so the plan fits beneath it, and a
     closed plan left a screen of black around the picture. */
  const [organizerOpen, setOrganizerOpen] = useState(true);
  /**
   * The phone viewing layout (spec 1a-1c). The stage is fixed from the
   * viewport snapshot: in landscape the plan opening or closing is the only
   * other input. Not the video's metadata, the save status, a toast or the
   * segment sheet, which is what kept moving it.
   */
  /**
   * The video's own size, from its metadata. The phone stage is its aspect
   * (rotation applied) bounded by the viewport, held once known. Before that
   * it is 16:9; the one change to the real size is animated and the picture
   * fades in with it (stageSettling).
   */
  const [mainVideoSize, setMainVideoSize] = useState<{ w: number; h: number } | null>(null);
  const [stageSettling, setStageSettling] = useState(false);
  const handleVideoSize = useCallback((w: number, h: number) => {
    setMainVideoSize((prev) => {
      if (prev && prev.w === w && prev.h === h) return prev;
      if (!prev) {
        setStageSettling(true);
        setTimeout(() => setStageSettling(false), 300);
      }
      return { w, h };
    });
  }, []);
  const mainAspect = mainVideoSize
    ? (videoRotation === 90 || videoRotation === 270 ? mainVideoSize.h / mainVideoSize.w : mainVideoSize.w / mainVideoSize.h)
    : undefined;
  const phoneView = viewingLayout(phoneViewport, organizerOpen, mainAspect);
  const phoneLandscape = isMobile && phoneViewport.landscape;
  /** Where the segment sheet docks on a phone (spec 1d). Memoised: the sheet re-places on change. */
  const segmentSheetDock = useMemo(
    () => (isMobile ? { landscape: phoneLandscape, railWidth: phoneLandscape ? landscapeSheetWidth(phoneViewport) : railWidth(phoneViewport), insets: phoneViewport.insets } : undefined),
    [isMobile, phoneLandscape, phoneViewport],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Brief post-submit success flash on the header button (~1.2s) before
  // onSubmitSuccess fires and the parent navigates away.
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ── Final-lesson progress-report modal ────────────────────────────────────────
  // Draft state for the four progress-report fields lives HERE (not inside
  // ProgressReportModal) so the "Save draft" close + reopen flow within the
  // same editor session preserves the answers. The modal is a controlled
  // component that receives values + setters as props. Draft state evaporates
  // when the editor unmounts (intentional session boundary — no localStorage,
  // no backend persistence).
  const [progressReportOpen, setProgressReportOpen] = useState(false);
  const [progressReportDraft, setProgressReportDraft] = useState({
    whatImproved: '',
    keepDoing: '',
    nextFocus: '',
    personalNote: '',
  });

  // ── Auto-save ─────────────────────────────────────────────────────────────────
  /* 'retrying': a save failed (or has not been confirmed for SAVE_STALL_MS)
     and is being tried again. 'error': refused outright, not retried. */
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'retrying' | 'error'>('idle');
  /** True when what is on screen differs from what was last written. */
  const [isDirty, setIsDirty] = useState(false);
  /**
   * Fingerprint of the last draft actually written, or null to adopt whatever
   * is in state as the baseline without writing it.
   *
   * This replaces a countdown of renders to skip. The counter could not tell
   * "this render is the echo of my own save" from "this render is the coach
   * adding a segment", so any edit made during a round trip was swallowed by
   * the skip the response queued — and the effect cleanup cancelled its timer
   * on the way past. Comparing against what was written cannot confuse the two.
   */
  const lastSavedKeyRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);
  /** Set when a save was wanted while one was already in flight. */
  const pendingSaveRef = useRef(false);
  /** The save in flight, so closing can wait for it. */
  const inFlightRef = useRef<Promise<void> | null>(null);
  const retryCountRef = useRef(0);
  const unmountedRef = useRef(false);
  /** When the newest change on screen was made (ISO): the local copy's stamp. */
  const editedAtRef = useRef(new Date(0).toISOString());
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveDraftRef = useRef(onSaveDraft);
  onSaveDraftRef.current = onSaveDraft;
  const buildDraftDataRef = useRef<(() => VideoEditorDraftData) | null>(null);
  const runSaveRef = useRef<(() => Promise<void>) | null>(null);
  /* Draft safety (draftGuard.ts). The refs let the [] callbacks below read
     the current values. */
  const draftReadyRef = useRef(draftReady);
  draftReadyRef.current = draftReady;
  /** True once this session has loaded a non-empty plan from the host. */
  const loadedNonEmptyRef = useRef(false);
  /** Set while a loaded draft's setters have not applied yet: that pass sees the old state. */
  const applyingDraftRef = useRef(false);
  /** Where the local copy of unconfirmed changes lives (pendingDraftStore.ts); none without a lesson. */
  const pendingKey = lessonId && originalVideoId ? `${lessonId}/${originalVideoId}` : null;
  const pendingKeyRef = useRef(pendingKey);
  useEffect(() => { pendingKeyRef.current = pendingKey; }, [pendingKey]);

  const { showSuccess, showError, showInfo } = useToast();
  const { currentUser } = useAuth();

  // ── Initialize from draft ─────────────────────────────────────────────────────
  useEffect(() => {
    if (initialDraft) {
      /* The baseline is the loaded draft itself, as it will be on screen, so
         opening writes nothing. It used to be dropped (null) for the next
         pass to adopt, but that pass runs in this same commit, before the
         setters below have applied: it adopted the empty plan, the loaded one
         then differed from it, and every open wrote the draft back (spec 12.2).
         The pass in this commit is skipped for the same reason. */
      lastSavedKeyRef.current = draftKey({
        notableSegments: initialDraft.notableSegments ?? [],
        voiceoverNotableSegmentClips: initialDraft.voiceoverNotableSegmentClips ?? [],
        notes: initialDraft.notes ?? '',
        coachNotes: initialDraft.coachNotes ?? '',
        audioMuted: initialDraft.audioMuted ?? false,
        attachedDrills: initialDraft.attachedDrills ?? [],
        videoRotation: initialDraft.videoRotation ?? 0,
      });
      applyingDraftRef.current = true;
      // Effects of one commit run back to back, so this clears it for the next commit only.
      queueMicrotask(() => { applyingDraftRef.current = false; });
      if (!isEmptyPlan(initialDraft)) loadedNonEmptyRef.current = true;
      setNotableSegments(initialDraft.notableSegments ?? []);
      setVoiceoverClips((current) => {
        // Never overwrite clips the coach has already recorded. The parent can
        // call setLoadedDraftData asynchronously (after the editor is already
        // open and recording has happened), and the draft it found was saved
        // before the clip was recorded — applying it would silently drop the clip.
        const hasRecorded = current.some((c) => c.videoBlob || c.videoStoragePath);
        if (hasRecorded) return current;
        return initialDraft.voiceoverNotableSegmentClips ?? [];
      });
      setNotes(initialDraft.notes ?? '');
      setCoachNotes(initialDraft.coachNotes ?? '');
      setAudioMuted(initialDraft.audioMuted ?? false);
      setAttachedDrills(initialDraft.attachedDrills ?? []);
      setVideoRotation((initialDraft.videoRotation as 0 | 90 | 180 | 270) ?? 0);
    }
  }, [initialDraft]);

  /** Arms the debounce. Always clears first, so this is the only writer of the timer. */
  const armSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => { void runSaveRef.current?.(); }, AUTOSAVE_DEBOUNCE_MS);
  }, []);

  const runSave = useCallback(async (): Promise<void> => {
    const save = onSaveDraftRef.current;
    const build = buildDraftDataRef.current;
    if (!save || !build) return;
    if (isSavingRef.current) {
      /* Do not drop it. The previous version returned here and nothing ever
         rescheduled, so a save that collided with one in flight was lost
         outright. */
      pendingSaveRef.current = true;
      return;
    }
    if (!savesAllowed(draftReadyRef.current, true)) return;
    const sent = build();
    /* Nothing to write: what is on screen is what was last written. */
    if (draftKey(sent) === lastSavedKeyRef.current) {
      setIsDirty(false);
      return;
    }
    const sentEditedAt = editedAtRef.current;
    isSavingRef.current = true;
    setSaveStatus('saving');
    const stall = setTimeout(() => setSaveStatus((st) => (st === 'saving' ? 'retrying' : st)), SAVE_STALL_MS);
    const attempt = (async () => {
      try {
        const updatedData = await save({ ...sent, allowEmptyOverwrite: loadedNonEmptyRef.current });
        retryCountRef.current = 0;
        /* The baseline is what was written, with the server's copies of the clips
           that were sent — not the post-merge state. Anything added during the
           round trip is therefore still a difference and still gets written. */
        lastSavedKeyRef.current = draftKey({
          ...sent,
          voiceoverNotableSegmentClips: updatedData.voiceoverNotableSegmentClips,
        });
        // Merge server response with current state: for each clip still present
        // locally, swap in the server version (which carries the storage path).
        // Clips deleted while the save was in flight are not in `current` and
        // must not be re-added — driving the merge from `current` enforces that.
        setVoiceoverClips((current) => {
          const serverById = new Map(
            updatedData.voiceoverNotableSegmentClips.map((c) => [c.id, c])
          );
          return current.map((c) => serverById.get(c.id) ?? c);
        });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus((st) => (st === 'saved' ? 'idle' : st)), 3000);
        /* Confirmed: the local copy can go, unless it holds a later change. */
        if (pendingKeyRef.current) void clearPending(pendingKeyRef.current, sentEditedAt);
        // Sync notable segments to player library (silent — no toast)
        if (playerId && currentUser && sent.notableSegments.length > 0) {
          const ctx = lessonId ? { type: 'lesson' as const, id: lessonId } : { type: 'admin' as const };
          syncSegmentsToPlayerLibrary(
            sent.notableSegments, playerId, ctx, originalVideoId ?? '',
            currentUser.uid, currentUser.displayName ?? '', lessonId,
          ).catch((err) => console.error('[playerLibrary sync]', err)); // fire-and-forget
        }
      } catch (err) {
        if (err instanceof EmptyDraftOverwriteError) {
          setSaveStatus('error');
        } else {
          /* Not saved: the local copy keeps the change, and it is tried again. */
          setSaveStatus('retrying');
          if (!unmountedRef.current) {
            const wait = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** retryCountRef.current);
            retryCountRef.current += 1;
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = setTimeout(() => { void runSaveRef.current?.(); }, wait);
          }
        }
      } finally {
        clearTimeout(stall);
        isSavingRef.current = false;
        inFlightRef.current = null;
        if (pendingSaveRef.current) {
          pendingSaveRef.current = false;
          armSave();
        }
      }
    })();
    inFlightRef.current = attempt;
    return attempt;
  }, [armSave, playerId, currentUser, lessonId, originalVideoId]);
  runSaveRef.current = runSave;

  // ── Auto-save trigger ─────────────────────────────────────────────────────────
  useEffect(() => {
    // Don't auto-save while the clip recording modal is open — the clip blob lives
    // only in ClipEditorModal state at this point and hasn't been handed back via
    // onSave yet. Saving now would omit the clip; when the modal closes the dep
    // change re-runs this effect with the clip already in voiceoverClips.
    /* Nor before the host has read the stored draft: no write, and no
       baseline taken either, so the loaded draft becomes the baseline. */
    if (applyingDraftRef.current) return;
    const build = buildDraftDataRef.current;
    const key = build ? draftKey(build()) : '';
    const action = decideAutosave({
      draftReady,
      hasSaver: !!onSaveDraftRef.current && !!build,
      /* Phone (spec 13.7): takes are handed over as they are saved and the
         recorder stays open, so they upload in the background meanwhile. */
      clipEditorOpen: showClipEditorModal && !isMobile,
      lastSavedKey: lastSavedKeyRef.current,
      key,
    });
    if (action === 'wait') return;
    if (action === 'adopt') {
      // Nothing written yet this session: adopt what is here rather than writing it back.
      lastSavedKeyRef.current = key;
      setIsDirty(false);
      return;
    }
    if (action === 'clean') {
      setIsDirty(false);
      return;
    }
    setIsDirty(true);
    /* Keep the change locally until a save of it is confirmed. */
    editedAtRef.current = new Date().toISOString();
    if (pendingKeyRef.current && build) void savePending(pendingKeyRef.current, build(), editedAtRef.current);
    armSave();
    /* Deliberately no cleanup. A cleanup that cancels the timer runs before the
       *next* effect pass whatever that pass decides, which is how the echo of a
       completed save used to cancel a write queued behind it. armSave clears
       before it arms, so the debounce still collapses; the timer is cancelled
       only on unmount, below.

     The deps are exhaustive now: everything else the pass touches is read
     through a ref, so the disable the old version needed is gone. */
  }, [voiceoverClips, notes, coachNotes, notableSegments, audioMuted, attachedDrills, videoRotation, showClipEditorModal, armSave, draftReady, isMobile]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      /* Spec 13.6: leaving the editor is the one place the recorder's camera
         and microphone stop. A no-op where nothing was granted (desktop). */
      releaseCapture();
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  /**
   * Leaving mid-edit: the page hidden (app switch, lock, tab change) or going
   * away. The debounce is skipped and the save starts now. If the page dies
   * before it is confirmed, the local copy (pendingDraftStore) still holds the
   * change and the next open puts it back and saves it.
   */
  useEffect(() => {
    const flush = () => {
      if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
      void runSaveRef.current?.();
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  /**
   * On open, once the stored draft is in: a local copy newer than it holds
   * changes whose save was never confirmed. They go back on screen and the
   * auto-save writes them. A copy the stored draft already covers is dropped.
   */
  const pendingCheckedRef = useRef(false);
  useEffect(() => {
    if (!draftReady || !pendingKey || pendingCheckedRef.current) return;
    pendingCheckedRef.current = true;
    const storedAt = initialDraft?.storedLastEditedAt;
    void readPending<VideoEditorDraftData>(pendingKey).then((pending) => {
      if (!pending || unmountedRef.current) return;
      if (!shouldApplyPending(pending.editedAt, storedAt)) {
        void clearPending(pendingKey, pending.editedAt);
        return;
      }
      /* Only onto an untouched plan: an edit made already is newer still. */
      const build = buildDraftDataRef.current;
      if (build && lastSavedKeyRef.current !== null && draftKey(build()) !== lastSavedKeyRef.current) return;
      const d = pending.data;
      setNotableSegments(d.notableSegments ?? []);
      setVoiceoverClips(d.voiceoverNotableSegmentClips ?? []);
      setNotes(d.notes ?? '');
      setCoachNotes(d.coachNotes ?? '');
      setAudioMuted(d.audioMuted ?? false);
      setAttachedDrills(d.attachedDrills ?? []);
      setVideoRotation((d.videoRotation as 0 | 90 | 180 | 270) ?? 0);
      showInfo('Restored changes that were not saved');
    });
  }, [draftReady, pendingKey, initialDraft, showInfo]);

  // ── Video controls ────────────────────────────────────────────────────────────
  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      videoPlayerRef.current?.pause();
    } else {
      videoPlayerRef.current?.play();
    }
  }, [isPlaying]);

  const handleSeek = useCallback((time: number) => {
    videoPlayerRef.current?.seek(time);
  }, []);

  // When set, the player auto-stops when currentTime reaches this value
  const playUntilRef = useRef<number | null>(null);

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
    if (playUntilRef.current !== null && time >= playUntilRef.current) {
      videoPlayerRef.current?.pause();
      playUntilRef.current = null;
    }
  }, []);

  const handlePlaySegment = useCallback((seg: NotableSegment) => {
    playUntilRef.current = seg.endTime;
    videoPlayerRef.current?.seek(seg.startTime);
    videoPlayerRef.current?.play();
  }, []);

  const handleToggleAudioMute = useCallback(() => {
    setAudioMuted((prev) => !prev);
  }, []);

  organizerOpenRef.current = organizerOpen;

  /**
   * Give the clip editor the full width, then hand the rail back exactly as it
   * was. Keyed on the clip editor alone: the coach can still reopen the rail
   * from the header while recording, and that must not be mistaken for the
   * state to restore.
   */
  useEffect(() => {
    /* Not on a phone: the recorder covers the whole screen there, and closing
       the plan behind it would resize the viewing stage underneath. */
    if (isMobile) return;
    if (showClipEditorModal) {
      if (organizerBeforeClipRef.current === null) {
        organizerBeforeClipRef.current = organizerOpenRef.current;
        setOrganizerOpen(false);
      }
    } else if (organizerBeforeClipRef.current !== null) {
      setOrganizerOpen(organizerBeforeClipRef.current);
      organizerBeforeClipRef.current = null;
    }
  }, [showClipEditorModal, isMobile]);

  // ── Retroactive segment capture ───────────────────────────────────────────
  /**
   * One press, after the point has happened. The coach is watching, not
   * pre-marking, so the window is taken backwards from the playhead rather
   * than being bounded by two presses.
   */
  const CAPTURE_SECONDS = 15;
  const handleCaptureSegment = useCallback(() => {
    /* One capture at a time. On a phone the controls bar stays live while the
       panel is open, so the flag can be pressed again; the open capture wins. */
    if (pendingSegmentBounds) return;
    // Under a second in, there is nothing behind the playhead worth taking.
    if (currentTime < 1) {
      showInfo('Nothing to capture yet');
      return;
    }
    if (isPlaying) videoPlayerRef.current?.pause();
    setPendingSegmentBounds({
      startTime: Math.max(0, currentTime - CAPTURE_SECONDS),
      endTime: currentTime,
    });
    /* "Part", not "Segment". A coach sees this while working and a player sees
       it in the delivered lesson; "segment" is our word for it, not theirs.
       New segments only — saved ones keep whatever they were named. */
    setSegmentNameInput(isMobile ? nextPartName(notableSegments.map((x) => x.label)) : `Part ${notableSegments.length + 1}`);
    /* Landscape (spec 13.14): the panel opens over the video at the right;
       the organizer is not opened and the video does not move. */
  }, [currentTime, isPlaying, notableSegments, showInfo, pendingSegmentBounds, isMobile]);

  const handleCancelCapture = useCallback(() => {
    setPendingSegmentBounds(null);
  }, []);

  const handleCreateHighlightFromInterval = useCallback((startTime: number, endTime: number) => {
    if (pendingSegmentBounds) return;
    if (isPlaying) videoPlayerRef.current?.pause();
    setPendingSegmentBounds({ startTime, endTime });
    setSegmentNameInput(isMobile ? nextPartName(notableSegments.map((x) => x.label)) : `Part ${notableSegments.length + 1}`);
  }, [isPlaying, notableSegments, pendingSegmentBounds, isMobile]);

  const handleSaveCapture = useCallback((startTime: number, endTime: number, label: string) => {
    const seg: NotableSegment = {
      id: `notable-seg-${Date.now()}`,
      label,
      startTime,
      endTime,
    };
    /* Phone (spec 11.5): a new segment goes at the end of the list, where the
       coach can then drag it; the desktop keeps them in time order. */
    setNotableSegments((prev) => (isMobile ? [...prev, seg] : [...prev, seg].sort((a, b) => a.startTime - b.startTime)));
    setPendingSegmentBounds(null);
    // Playback is not resumed: the coach decides when to carry on.
    showSuccess(`"${seg.label}" saved (${formatTime(startTime)} – ${formatTime(endTime)})`);
  }, [showSuccess, isMobile]);


  /** Opens an existing segment directly in the Clip Editor (single-video recording) */
  const handleOpenSegmentInClipEditor = useCallback((seg: NotableSegment) => {
    setClipEditorPrimarySource({ videoUrl: videoUrl ?? '', segment: seg, label: seg.label, rotation: videoRotation || undefined });
    setClipEditorSecondarySource(null);
    setShowClipEditorModal(true);
  }, [videoUrl, videoRotation]);

  /** Called from ClipEditorModal's "Add video source" button — opens the dedicated comparison picker */
  const handleAddSecondarySourceToClipEditor = useCallback(() => {
    setComparisonDefaultTab(undefined);
    setShowComparisonSourceModal(true);
  }, []);

  // ── Update notable segment end time (from timeline drag) ──────────────────────
  // Frozen if segment is used in any clip — length is locked in that case
  const handleUpdateNotableSegmentEndTime = useCallback((segmentId: string, newEndTime: number) => {
    const isUsedInClip = voiceoverClips.some((c) => c.segmentIds.includes(segmentId));
    if (isUsedInClip) return;
    setNotableSegments((prev) =>
      prev.map((seg) => (seg.id === segmentId ? { ...seg, endTime: newEndTime } : seg))
    );
  }, [voiceoverClips]);

  // ── Update notable segment label/note (from Segments tab inline edit) ─────────
  const handleUpdateSegment = useCallback((segId: string, update: Partial<Pick<NotableSegment, 'label' | 'note'>>) => {
    setNotableSegments((prev) => {
      const idx = prev.findIndex((s) => s.id === segId);
      if (idx === -1) return prev; // not in this draft — no-op, keeps same reference
      return prev.map((seg) => (seg.id === segId ? { ...seg, ...update } : seg));
    });
  }, []);

  // ── Delete notable segment ────────────────────────────────────────────────────
  const handleDeleteNotableSegment = useCallback((segmentId: string) => {
    const clipCount = voiceoverClips.filter((c) => c.segmentIds.includes(segmentId)).length;
    if (clipCount > 0) {
      setShowNotableSegDeleteConfirm({ segmentId, clipCount });
    } else {
      setNotableSegments((prev) => prev.filter((s) => s.id !== segmentId));
      showInfo('Segment deleted');
    }
  }, [voiceoverClips, showInfo]);

  const handleConfirmDeleteNotableSegment = useCallback(() => {
    if (!showNotableSegDeleteConfirm) return;
    const { segmentId } = showNotableSegDeleteConfirm;
    setNotableSegments((prev) => prev.filter((s) => s.id !== segmentId));
    setVoiceoverClips((prev) => prev.filter((c) => !c.segmentIds.includes(segmentId)));
    setShowNotableSegDeleteConfirm(null);
    showInfo('Segment and associated clips deleted');
  }, [showNotableSegDeleteConfirm, showInfo]);


  // ── Clip management ───────────────────────────────────────────────────────────
  const handleSaveClip = useCallback((clip: VoiceoverNotableSegmentClip) => {
    setVoiceoverClips((prev) => [...prev, clip]);
    showSuccess(isMobile ? 'Clip saved' : 'Clip saved!');
  }, [showSuccess, isMobile]);
  /* Phone (spec 10.4): a take saved while the recorder stays open gets the
     recorder's own small "Clip saved" toast, not this one as well. */
  /* Spec 13.7: the take is kept on the device (the local copy, with its file)
     before the recorder shows "Clip saved"; the upload follows in the
     background, and a failed or interrupted one never drops it. */
  const handleSaveClipAndStay = useCallback(async (clip: VoiceoverNotableSegmentClip) => {
    if (!isMobile) { handleSaveClip(clip); return; }
    setVoiceoverClips((prev) => [...prev, clip]);
    const key = pendingKeyRef.current;
    const build = buildDraftDataRef.current;
    if (!key || !build) return;
    const data = build();
    editedAtRef.current = new Date().toISOString();
    await savePending(key, { ...data, voiceoverNotableSegmentClips: [...data.voiceoverNotableSegmentClips, clip] }, editedAtRef.current);
  }, [handleSaveClip, isMobile]);

  const handleDeleteClip = useCallback((clipId: string) => {
    setVoiceoverClips((prev) => prev.filter((c) => c.id !== clipId));
    showInfo('Clip deleted');
  }, [showInfo]);

  const handleRenameClip = useCallback((clipId: string, newLabel: string) => {
    setVoiceoverClips((prev) => prev.map((c) => c.id === clipId ? { ...c, label: newLabel } : c));
  }, []);

  // ── Add recording for segment (opens Clip Editor with segment as primary) ──────
  const handleAddRecordingForSegment = useCallback((segmentId: string) => {
    const seg = notableSegments.find((s) => s.id === segmentId);
    if (!seg) return;
    handleOpenSegmentInClipEditor(seg);
  }, [notableSegments, handleOpenSegmentInClipEditor]);

  // ── Add side-by-side for segment ──────────────────────────────────────────────
  const handleAddSideBySideForSegment = useCallback((
    segmentId: string,
    sourceType: 'camera' | 'upload' | 'currentVideo' | 'playerLibrary' | 'drillLibrary' | 'proLibrary',
  ) => {
    const primarySeg = notableSegments.find((s) => s.id === segmentId);
    if (!primarySeg) return;
    const primary: ClipSource = { videoUrl: videoUrl ?? '', segment: primarySeg, label: primarySeg.label, rotation: videoRotation || undefined };

    if (sourceType === 'camera') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource({ videoUrl: '', isCameraSource: true, label: 'Camera' });
      setShowClipEditorModal(true);
    } else if (sourceType === 'upload') {
      pendingUploadSegmentIdRef.current = segmentId;
      uploadFileInputRef.current?.click();
    } else if (sourceType === 'currentVideo') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource(null);
      setComparisonDefaultTab('current');
      openClipEditorAfterComparisonRef.current = true;
      setShowComparisonSourceModal(true);
    } else if (sourceType === 'playerLibrary') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource(null);
      setComparisonDefaultTab('player');
      openClipEditorAfterComparisonRef.current = true;
      setShowComparisonSourceModal(true);
    } else if (sourceType === 'drillLibrary' || sourceType === 'proLibrary') {
      setClipEditorPrimarySource(primary);
      setClipEditorSecondarySource(null);
      setComparisonDefaultTab(sourceType === 'drillLibrary' ? 'drill-library' : 'pro-library');
      openClipEditorAfterComparisonRef.current = true;
      setShowComparisonSourceModal(true);
    }
  }, [notableSegments, videoUrl, videoRotation]);

  // ── Reorder segments ──────────────────────────────────────────────────────────
  const handleReorderSegments = useCallback((reordered: NotableSegment[]) => {
    setNotableSegments(reordered);
    /**
     * Playback reads voiceoverClips in array order, so reordering the segment
     * list alone changed the rail and nothing else — the coach saw a new order
     * and the delivered lesson played the old one.
     *
     * Sorted here, at the drag, rather than derived from notableSegments at
     * read time. A delivered lesson plays from its saved array, and a coach may
     * have reordered before delivery, so deriving at read time would change
     * what an already-delivered lesson plays. Sorting on the drag only ever
     * affects the draft in front of the coach.
     *
     * Stable and partial: clips with no segment hold the index they already
     * occupy, because a camera recording was made at a point in the lesson and
     * a drag elsewhere is no reason to move it. Everything else is grouped by
     * segmentIds[0] in the new order, keeping recording order within a segment.
     */
    setVoiceoverClips((clips) => {
      const rank = new Map(reordered.map((seg, i) => [seg.id, i]));
      const movableIdx: number[] = [];
      clips.forEach((c, i) => { if (c.segmentIds[0] !== undefined) movableIdx.push(i); });
      const sorted = movableIdx
        .map((i) => ({ i, clip: clips[i] }))
        .sort((a, b) => {
          const ra = rank.get(a.clip.segmentIds[0]) ?? Number.MAX_SAFE_INTEGER;
          const rb = rank.get(b.clip.segmentIds[0]) ?? Number.MAX_SAFE_INTEGER;
          return ra !== rb ? ra - rb : a.i - b.i;   // ties keep recording order
        })
        .map((e) => e.clip);
      const next = [...clips];
      movableIdx.forEach((slot, k) => { next[slot] = sorted[k]; });
      return next;
    });
  }, []);

  // ── Draft data builder ────────────────────────────────────────────────────────
  const buildDraftData = useCallback((): VideoEditorDraftData => ({
    notableSegments,
    voiceoverNotableSegmentClips: voiceoverClips,
    notes,
    coachNotes,
    audioMuted,
    attachedDrills,
    videoRotation,
  }), [notableSegments, voiceoverClips, notes, coachNotes, audioMuted, attachedDrills, videoRotation]);
  buildDraftDataRef.current = buildDraftData;

  const hasClips = voiceoverClips.some((c) => c.videoBlob || c.videoStoragePath);

  // Clips that can actually be previewed (have video data)
  const previewableClips = voiceoverClips.filter((c) => c.videoBlob || c.videoStoragePath);

  // Resolve blob URL for the individual clip preview modal
  useEffect(() => {
    if (!previewClipId) { setPreviewClipUrl(null); return; }
    const clip = voiceoverClips.find((c) => c.id === previewClipId);
    if (!clip) { setPreviewClipId(null); return; }
    if (clip.videoBlob) {
      const url = URL.createObjectURL(clip.videoBlob);
      setPreviewClipUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (clip.videoStoragePath) {
      setPreviewClipLoading(true);
      getDownloadURL(storageRef(storage, clip.videoStoragePath))
        .then((url) => setPreviewClipUrl(url))
        .catch(() => setPreviewClipUrl(null))
        .finally(() => setPreviewClipLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewClipId]);

  // Resolve blob URL for the current lesson preview clip
  useEffect(() => {
    if (!previewLessonOpen) { setPreviewLessonUrl(null); return; }
    const clip = previewableClips[previewLessonIdx];
    if (!clip) { setPreviewLessonUrl(null); return; }
    if (clip.videoBlob) {
      const url = URL.createObjectURL(clip.videoBlob);
      setPreviewLessonUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (clip.videoStoragePath) {
      getDownloadURL(storageRef(storage, clip.videoStoragePath))
        .then((url) => setPreviewLessonUrl(url))
        .catch(() => setPreviewLessonUrl(null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLessonOpen, previewLessonIdx]);

  // ── App chrome suppression ────────────────────────────────────────────────────
  // Belt and braces alongside the portal. The portal is meant to put this
  // overlay outside Layout's subtree so no ancestor stacking context can trap
  // it, but that has proven fragile in practice, so this removes the chrome
  // outright instead of out-painting it. index.css hides #app-top-bar and
  // #mobile-bottom-nav while the attribute is present.
  //
  // Cleanup runs on ANY unmount, so the chrome returns whether the coach used
  // the back control, submitted the lesson, or the parent unmounted the editor
  // some other way.
  useEffect(() => {
    openEditorCount += 1;
    document.body.setAttribute('data-editor-open', 'true');
    return () => {
      openEditorCount -= 1;
      if (openEditorCount <= 0) {
        openEditorCount = 0;
        document.body.removeAttribute('data-editor-open');
      }
    };
  }, []);

  // ── Close with save ───────────────────────────────────────────────────────────
  // Flushes any pending debounced auto-save before calling onCancel so that
  // changes made within the 1.5 s debounce window are not lost on quick close.
  const handleClose = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    /* Closing before the draft has loaded writes nothing: what is on screen
       is not the coach's plan yet. Otherwise the last change is saved: after
       the save in flight, if there is one, rather than skipped because of it.
       Closing waits up to CLOSE_WAIT_MS; a slower save carries on behind, and
       the local copy covers it if it never lands. */
    if (onSaveDraft && savesAllowed(draftReadyRef.current, true)) {
      const flush = (async () => {
        while (inFlightRef.current) await inFlightRef.current.catch(() => {});
        await runSaveRef.current?.();
      })();
      await Promise.race([flush, new Promise((r) => setTimeout(r, CLOSE_WAIT_MS))]);
    }
    onCancel();
  }, [onCancel, onSaveDraft]);

  /* Spec 13.7: on a phone, leaving while a take is still uploading asks
     first. Leave Anyway is safe: the take is kept on the device and uploads
     the next time the lesson opens. */
  const [leaveWhileUploading, setLeaveWhileUploading] = useState(false);
  const requestClose = useCallback(() => {
    if (isMobile && voiceoverClips.some((c) => c.videoBlob && !c.videoStoragePath)) {
      setLeaveWhileUploading(true);
      return;
    }
    void handleClose();
  }, [isMobile, voiceoverClips, handleClose]);

  // ── Render ────────────────────────────────────────────────────────────────────

  /* Previews on a phone (spec 5): full screen inside the insets, a 44 top
     bar, a stage fixed at open and the prev/next row pinned at the bottom. */
  const phoneSheetStyle: React.CSSProperties = {
    background: 'var(--editor-bg)',
    paddingLeft: 'env(safe-area-inset-left, 0px)',
    paddingRight: 'env(safe-area-inset-right, 0px)',
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  };
  const clipPreviewStage = previewStage(phoneViewport, false, previewAspect ?? undefined);
  const lessonPreviewStage = previewStage(phoneViewport, previewableClips.length > 1, previewAspect ?? undefined);
  /* Spec 13.5, 13.16: on a phone the lesson preview has its own body
     (PhoneLessonPreview), in both orientations, so the video element is
     never remounted when the phone turns. */
  const phonePreviewBody = isMobile;

  /* One status, shown in the header row on desktop and under the name on a
     phone. */
  const saveStatusClass =
    saveStatus === 'error' || saveStatus === 'retrying' ? 'text-[var(--editor-danger)]' :
    saveStatus === 'saving' || isDirty || saveStatus === 'saved'
      ? 'text-[var(--editor-text-muted)]'
      : 'text-transparent';
  const saveStatusLabel =
    saveStatus === 'error'  ? 'Save failed' :
    saveStatus === 'retrying' ? 'Not saved, retrying' :
    saveStatus === 'saving' ? 'Saving…' :
    isDirty                 ? 'Unsaved changes' :
    saveStatus === 'saved'  ? 'Saved' : '·';
  /* Phone (spec 10.1): "Saving" holds for at least 600ms. Spec 12.1: a
     change reads "Saving" until the server confirms it, and a save that
     failed reads "Not saved, retrying" while it is tried again. */
  const phoneSaving = useHeldFlag(saveStatus === 'saving', 600);
  const phoneSaveLabel =
    saveStatus === 'error' ? 'Save failed' :
    saveStatus === 'retrying' ? 'Not saved, retrying' :
    phoneSaving || isDirty ? 'Saving' : 'Saved';

  const viewZoom = usePinchZoom({ enabled: isMobile && !showClipEditorModal, surfaceRef: viewStageRef, targetRef: viewZoomRef });

  /* ── Landscape viewing controls (spec 13.14) ──
     Floating over the video. A quick one-finger tap on the video shows or
     hides them (200ms fade); they hide 3s after playback starts or after
     the last touch on them, and stay while paused, while scrubbing, and
     while the plan or a sheet is open. A tap never changes playback. */
  const [viewScrubbing, setViewScrubbing] = useState(false);
  /* Spec 14.10: the one-time "Swipe up for full screen" hint, the first time
     the editor is in landscape with Safari's bars showing (the visible
     height clearly short of the screen's). Never in a home screen app. Seen
     is remembered on the device, and for this page in case storage is
     blocked. It fades after 4s or on the first swipe. */
  const [barsHint, setBarsHint] = useState<'off' | 'on' | 'fading'>('off');
  useEffect(() => {
    if (!phoneLandscape || barsHintShownThisPage) return;
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (standalone) return;
    try { if (localStorage.getItem('lve.barsHintSeen') === '1') return; } catch { /* treat as not seen */ }
    const vv = window.visualViewport;
    if (!vv) return;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    let offTimer: ReturnType<typeof setTimeout> | null = null;
    const dismiss = () => {
      if (offTimer) return;
      setBarsHint('fading');
      offTimer = setTimeout(() => setBarsHint('off'), 300);
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('touchmove', dismiss);
    };
    const check = () => {
      if (barsHintShownThisPage) return;
      const screenH = Math.min(window.screen.width, window.screen.height);
      if (vv.height >= screenH - 40) return;
      barsHintShownThisPage = true;
      try { localStorage.setItem('lve.barsHintSeen', '1'); } catch { /* remembered for this page only */ }
      setBarsHint('on');
      fadeTimer = setTimeout(dismiss, 4000);
      window.addEventListener('scroll', dismiss, { passive: true });
      window.addEventListener('touchmove', dismiss, { passive: true });
    };
    const raf = requestAnimationFrame(check);
    vv.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', check);
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('touchmove', dismiss);
      if (fadeTimer) clearTimeout(fadeTimer);
      if (offTimer) clearTimeout(offTimer);
    };
  }, [phoneLandscape]);

  /* Spec 14.3: the top-left corner menu (close, player, status). */
  const [cornerOpen, setCornerOpen] = useState(false);
  const cornerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!cornerOpen) return;
    const t = setTimeout(() => setCornerOpen(false), 4000);
    const outside = (e: PointerEvent) => {
      if (cornerRef.current && !cornerRef.current.contains(e.target as Node)) setCornerOpen(false);
    };
    document.addEventListener('pointerdown', outside, true);
    return () => { clearTimeout(t); document.removeEventListener('pointerdown', outside, true); };
  }, [cornerOpen]);
  const [chromeShown, setChromeShown] = useState(true);
  const chromeHeld = !isPlaying || viewScrubbing || organizerOpen || !!pendingSegmentBounds
    || playerCardOpen || showSubmitConfirmation || showComparisonSourceModal || cornerOpen;
  const chromeVisible = chromeShown || chromeHeld;
  const chromeHeldRef = useRef(chromeHeld);
  useEffect(() => { chromeHeldRef.current = chromeHeld; });
  const chromeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armChromeHide = useCallback(() => {
    if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
    chromeTimerRef.current = setTimeout(() => { if (!chromeHeldRef.current) setChromeShown(false); }, 3000);
  }, []);
  const pokeChrome = useCallback(() => { setChromeShown(true); armChromeHide(); }, [armChromeHide]);
  useEffect(() => { if (phoneLandscape && isPlaying) armChromeHide(); }, [phoneLandscape, isPlaying, armChromeHide]);
  useEffect(() => () => { if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current); }, []);
  /* One finger, no movement, under 250ms; a second finger, a drag or a
     touch on a control is not a tap. */
  const viewTapRef = useRef<{ t: number; x: number; y: number; ok: boolean } | null>(null);
  const onStageTouchStart = useCallback((e: React.TouchEvent) => {
    if (!phoneLandscape) return;
    if (e.touches.length !== 1 || (e.target as Element).closest('button')) { if (viewTapRef.current) viewTapRef.current.ok = false; return; }
    const t = e.touches[0];
    viewTapRef.current = { t: performance.now(), x: t.clientX, y: t.clientY, ok: true };
  }, [phoneLandscape]);
  const onStageTouchMove = useCallback((e: React.TouchEvent) => {
    const a = viewTapRef.current;
    if (!a) return;
    const t = e.touches[0];
    if (e.touches.length !== 1 || !t || Math.hypot(t.clientX - a.x, t.clientY - a.y) > 10) a.ok = false;
  }, []);
  const onStageTouchEnd = useCallback((e: React.TouchEvent) => {
    const a = viewTapRef.current;
    if (!a || e.touches.length > 0) return;
    viewTapRef.current = null;
    if (!a.ok || performance.now() - a.t >= 250) return;
    if (chromeHeldRef.current) { pokeChrome(); return; }
    setChromeShown((v) => {
      if (!v) armChromeHide();
      return !v;
    });
  }, [pokeChrome, armChromeHide]);
  const beginViewScrub = useCallback(() => { setViewScrubbing(true); beginTransportScrub(); }, [beginTransportScrub]);
  const endViewScrub = useCallback(() => { setViewScrubbing(false); endTransportScrub(); pokeChrome(); }, [endTransportScrub, pokeChrome]);

  /* ── Phone viewing controls (spec 9.2) ──
     Portrait: the scrubber (Timeline, with its segment bars) full width in a
     32 row with the times at its ends, then one 44 icon row: mute left;
     back 5s, play and forward 5s centred; flag and rotate right. Landscape:
     all of it in one 44 strip under the stage. Timeline's own play button and
     time readout are hidden by phone CSS (.lve-scrub-only), so the scrubber
     is the only thing it draws here. */
  const phoneIconColor = (on: boolean) => ({ color: on ? 'var(--editor-text)' : 'var(--editor-text-muted)' });
  const phoneTimeText = 'flex-none text-[12px] leading-[16px] font-mono tabular-nums';
  const phoneScrubber = (
    /* px-2.5: the thumb is 20 and centred on the track's ends, so the track
       starts 10 in and the thumb never sits on a time label. */
    <div className="flex-1 min-w-0 grid content-center px-2.5 lve-phone-timeline lve-scrub-only">
      <Timeline
        duration={duration}
        currentTime={currentTime}
        slowMotionSegments={notableSegments}
        playIntervals={lessonRequest?.playerVideoPlayIntervals}
        onCreateHighlightFromInterval={handleCreateHighlightFromInterval}
        isPlaying={isPlaying}
        onSeek={handleSeek}
        onScrubStart={beginViewScrub}
        onScrubEnd={endViewScrub}
        onTogglePlay={handleTogglePlay}
        onUpdateSloMoSegmentEndTime={handleUpdateNotableSegmentEndTime}
      />
    </div>
  );
  const phoneMuteBtn = (
    <button onClick={handleToggleAudioMute} className={EBTN_ICON} style={phoneIconColor(!audioMuted)}
      title={audioMuted ? 'Original audio muted (tap to unmute)' : 'Original audio on (tap to mute)'}
      aria-label={audioMuted ? 'Unmute' : 'Mute'}>
      {audioMuted ? (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
      ) : (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
      )}
    </button>
  );
  const phoneRewindBtn = (
      <button onClick={() => handleSeek(Math.max(0, currentTime - 5))} className={EBTN_ICON} style={phoneIconColor(true)} title="Rewind 5 seconds" aria-label="Rewind 5 seconds">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/><text x="7" y="15.5" fontSize="6" fontWeight="bold" fill="currentColor">5</text></svg>
      </button>
  );
  const phonePlayBtn = (
      <button onClick={handleTogglePlay} className={EBTN_ICON} style={phoneIconColor(true)} aria-label={isPlaying ? 'Pause' : 'Play'} title={isPlaying ? 'Pause' : 'Play'} data-testid="phone-play-btn">
        {isPlaying ? (
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>
        ) : (
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        )}
      </button>
  );
  const phoneForwardBtn = (
      <button onClick={() => handleSeek(Math.min(duration, currentTime + 5))} className={EBTN_ICON} style={phoneIconColor(true)} title="Forward 5 seconds" aria-label="Forward 5 seconds">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/><text x="8" y="15.5" fontSize="6" fontWeight="bold" fill="currentColor">5</text></svg>
      </button>
  );
  const phoneCentreBtns = <>{phoneRewindBtn}{phonePlayBtn}{phoneForwardBtn}</>;
  const phoneRightBtns = (
    <>
      <button data-testid="highlight-tool-btn" data-current-time={currentTime} onClick={handleCaptureSegment} className={EBTN_ICON} style={{ color: 'var(--color-biscayne)' }} title="Save the last 15 seconds" aria-label="Save the last 15 seconds">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>
      </button>
      <button onClick={() => setVideoRotation(prev => ((prev + 90) % 360) as 0 | 90 | 180 | 270)} className={EBTN_ICON}
        style={videoRotation !== 0 ? { color: 'var(--color-db-oasis)' } : phoneIconColor(false)}
        title={videoRotation !== 0 ? `Video rotated ${videoRotation}° (tap to rotate more)` : 'Rotate video 90° clockwise'} aria-label="Rotate video clockwise">
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zm-3.04 5.47l1.42 1.42c1.16-1.16 1.71-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.47z"/></svg>
      </button>
    </>
  );
  const phoneTransport = phoneLandscape ? (
    /* Spec 13.14: one glass pill along the bottom, inside the inset rule:
       play, back 5, forward 5, the time, the timeline with its segment bars,
       the length, mute, flag and rotate. */
    <div ref={setTransportEl} className="lve-glass lve-pill lve-hit flex items-center gap-0.5 px-1.5 w-full" onPointerDown={pokeChrome} data-testid="landscape-controls-pill">
      {phonePlayBtn}
      {phoneRewindBtn}
      {phoneForwardBtn}
      <span className={`${phoneTimeText} ml-1`} style={{ color: 'var(--editor-text)' }}>{formatTime(currentTime)}</span>
      {phoneScrubber}
      <span className={phoneTimeText} style={{ color: 'var(--editor-text-muted)' }}>{formatTime(duration)}</span>
      {phoneMuteBtn}
      {phoneRightBtns}
    </div>
  ) : (
    <div ref={setTransportEl} className="flex-none flex flex-col" style={{ height: phoneView.transport, background: 'var(--editor-timeline)', borderTop: '1px solid var(--editor-border)' }}>
      <div className="flex items-center gap-2 px-3" style={{ height: 32 }}>
        <span className={phoneTimeText} style={{ color: 'var(--editor-text)' }}>{formatTime(currentTime)}</span>
        {phoneScrubber}
        <span className={phoneTimeText} style={{ color: 'var(--editor-text-muted)' }}>{formatTime(duration)}</span>
      </div>
      <div className="flex items-center px-2" style={{ height: 44 }}>
        <div className="flex-1 basis-0 flex items-center">{phoneMuteBtn}</div>
        <div className="flex-none flex items-center gap-6">{phoneCentreBtns}</div>
        <div className="flex-1 basis-0 flex items-center justify-end gap-2">{phoneRightBtns}</div>
      </div>
    </div>
  );

  // Portalled to document.body. The editor is mounted deep inside a page, and
  // Layout wraps every page in PageTransition — a motion.div that becomes a
  // stacking context while it animates opacity. That trapped this overlay's
  // z-index inside it, letting the 72px top bar (z-50, a sibling of the whole
  // page in the root context) paint over a z-10000 editor. Portalling puts the
  // editor outside Layout's subtree entirely, so no app chrome can overlap it
  // on either breakpoint, and it clears the z-9999 mobile bottom nav too.
  // Same escape hatch ProgressReportModal already uses.
  return createPortal(
    <>
    {/* Keyboard up on a phone: an opaque cover well beyond the viewport in
        both directions, under the editor, so however iOS scrolls the page to
        show the field, the lessons page never shows through. */}
    {isMobile && keyboardUp && (
      <div aria-hidden="true" className="fixed inset-x-0" style={{ top: '-100dvh', height: '300dvh', background: 'var(--editor-bg)', zIndex: 'var(--z-editor-root)' }} />
    )}
    {/* Phone (spec 13.12): sized to the visible viewport (.lve-fill in
        index.css: --vv-top and --vv-height, 100dvh as the fallback), so
        Safari's toolbars and tab bar showing and hiding never squeeze the
        video or the timeline. Desktop: inset 0, as before. */}
    <div ref={editorRootRef} className="fixed inset-0 lve-fill overflow-hidden" style={{ background: phoneLandscape ? '#000' : 'var(--editor-bg)', zIndex: 'var(--z-editor-root)', ...(isMobile ? { overscrollBehavior: 'none' } : {}) }}>
      {/* Landscape (spec 13.14): edge to edge on black; the controls keep
          the safe areas through the one inset rule (13.13) instead. */}
      <div className="flex flex-col h-full w-full overflow-hidden"
        style={phoneLandscape ? undefined : {
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
      <div className="relative flex flex-col flex-1 overflow-hidden">

        {/* ── Header ── */}
        {/* A fixed height on a phone (56 portrait, 44 landscape), so the save
            status line under the name can never change what sits below. */}
        {isMobile ? (phoneLandscape ? null : (
          /* Phone (spec 10.1): one 44 bar in portrait; landscape floats its
             controls over the video instead (spec 13.14). Close on the
             left; the player, then the save status on the same line,
             centred; in landscape the plan toggle, then Submit. The two
             sides take equal shares so the middle stays centred whatever is
             on the right. */
          <div className="flex items-center flex-none px-1" style={{ height: PHONE_HEADER, background: 'var(--editor-header)', borderBottom: '1px solid var(--editor-border)' }}>
            <div className="flex-1 basis-0 min-w-0 flex items-center">
              {!showClipEditorModal && (
                <button type="button" onClick={requestClose} className={EBTN_ICON} aria-label="Close" title="Close">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex-none max-w-[56%] min-w-0 flex justify-center">
              <button
                type="button"
                ref={playerChipRef}
                onClick={() => {
                  if (!titleFirstName) return;
                  const r = playerChipRef.current?.getBoundingClientRect();
                  if (r) { setPlayerCardAnchor({ x: r.left + r.width / 2, y: r.bottom + 8 }); setPlayerCardBox({ top: r.top, bottom: r.bottom + 4, left: r.left, right: r.right }); }
                  setPlayerCardOpen((v) => !v);
                }}
                aria-haspopup="dialog"
                aria-expanded={playerCardOpen}
                data-testid="header-player-name"
                className="relative flex items-center gap-2 min-w-0 max-w-full rounded-full px-1.5 ios-press after:absolute after:content-[''] after:inset-x-0 after:-inset-y-2"
                style={{ height: 32 }}
              >
                {titleFirstName && (playerAvatarUrl ? (
                  <img src={playerAvatarUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold" style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}>
                    {(playerName || titleFirstName).charAt(0).toUpperCase()}
                  </span>
                ))}
                {/* Subhead 15/20: the name (semibold), then " · " and the
                    status in grey. A long name wraps rather than being cut;
                    the status follows it. "Saving" holds for 600ms (useHeldFlag). */}
                <span className="flex flex-wrap items-baseline min-w-0 text-[15px] leading-[20px]">
                  <span className="font-semibold min-w-0 [overflow-wrap:anywhere]" style={{ color: 'var(--editor-text)' }}>
                    {titleFirstName || displayTitle}
                  </span>
                  {onSaveDraft && (
                    <span className={`flex-none ${saveStatus === 'error' || saveStatus === 'retrying' ? 'text-[var(--editor-danger)]' : 'text-[var(--editor-text-muted)]'}`} data-testid="phone-save-status">
                      {/* Spec 11.9: "Loading" until the stored plan has arrived;
                          nothing is saved before then, so "Saved" would mislead. */}
                      {'\u00a0· '}{!draftReady ? 'Loading' : phoneSaveLabel}
                    </span>
                  )}
                </span>
              </button>
            </div>
            <div className="flex-1 basis-0 min-w-0 flex items-center justify-end pr-1">
              {phoneLandscape && (
                <>
                  <button
                    type="button"
                    onClick={() => setOrganizerOpen(prev => !prev)}
                    aria-pressed={organizerOpen}
                    title={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
                    aria-label={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
                    className="relative w-8 h-8 flex-none rounded-full inline-flex items-center justify-center ios-press after:absolute after:content-[''] after:-inset-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
                    style={{ background: organizerOpen ? 'var(--editor-surface)' : 'transparent', color: organizerOpen ? 'var(--editor-text)' : 'var(--editor-text-muted)' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                    </svg>
                  </button>
                  <div className="w-3 flex-none" />
                </>
              )}
              {onSubmitDraft && (
                <button
                  data-testid="lve-submit-btn"
                  onClick={() => {
                    if (isFinalProgramLesson && onFinalSubmit) setProgressReportOpen(true);
                    else setShowSubmitConfirmation(true);
                  }}
                  disabled={!hasClips || isSubmitting || submitSuccess}
                  aria-busy={isSubmitting || submitSuccess}
                  className={EBTN_PRIMARY}
                  /* 32 tall, hugging its label; Subhead 15/20 (spec 10.1). The
                     recipe's ::after keeps the target 44. */
                  style={{ height: 32, fontSize: 15, lineHeight: '20px', paddingLeft: 12, paddingRight: 12 }}
                >
                  {isSubmitting || submitSuccess
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-label="Sending" />
                    : 'Submit'}
                </button>
              )}
            </div>
          </div>
        )) : (
          <div className="relative px-4 py-2.5 flex items-center gap-3 phone:px-2 phone:gap-1 phone:py-0 flex-shrink-0" style={{ height: isMobile ? phoneView.header : undefined, background: 'var(--editor-header)', borderBottom: '1px solid var(--editor-border)' }}>
            {/* Leave the editor. Returns to the lesson detail underneath — this
                overlay is local state, so there is nothing to navigate. Restores
                the previously commented-out Cancel: without it the coach's only
                exit was the browser back gesture, which skips every URL-less
                overlay and pops the last real route (Messages, if that is where
                they came from). handleClose flushes the pending
                auto-save first, so no edits are lost on the way out. */}
            {/* Hidden while the clip editor is open. Two unlabelled controls 60px
                apart, one closing a layer and one leaving the lesson, is the
                dangerous pair; hiding this one also guarantees the two are never
                both on screen at any width. Safe to hide: the clip editor's own
                close is unconditionally setShowClipEditorModal(false), so it
                always returns here. */}
            {/* Conditionally rendered rather than toggled with `hidden`: the
                recipe sets `inline-flex`, and `hidden` beside it is a coin flip
                on which display utility Tailwind emits last. Same result, no
                dependence on stylesheet order. */}
            {!showClipEditorModal && (
            <button
              type="button"
              onClick={requestClose}
              aria-label="Back to Lessons"
              title="Back to Lessons"
              /* The control exits to the orders list, so it is "lessons" plural:
                 the coach is already inside the lesson. The 44 target now comes
                 from the recipe's ::after rather than from min-h, which is what
                 lets this row drop from 64 to 56 without losing the target. */
              className={`${EBTN_GHOST} mr-1`}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span className="hidden md:inline phone:hidden text-sm">Back to Lessons</span>
            </button>
            )}

            {/* Pushes the actions cluster hard right. The title is absolutely
                positioned and out of flow, so it no longer does this itself.
                On a phone the title is in flow and takes this job back. */}
            <div className="flex-1 phone:hidden" />

            {/* Centred on the header, not on what is left over between the
                clusters: absolute positioning means the title stays put whatever
                the left and right widths do, and max-w keeps it clear of them at
                narrow widths rather than colliding. */}
            {/* On a phone, in flow between the back control and the actions:
                centred on the header it ran under the plan and send buttons.
                Here it can only take the width that is left, and truncates. */}
            <div className="absolute left-1/2 -translate-x-1/2 max-w-[46%] md:max-w-[52%] pointer-events-none phone:static phone:translate-x-0 phone:max-w-none phone:flex-1 phone:min-w-0">
              {/* One size, one weight, one colour across both halves. Splitting
                  the treatment made the type and the name read as two unrelated
                  elements sitting next to each other rather than as one title.
                  The pill below carries the affordance on its own. */}
              {/* Baseline-aligned, not box-aligned.
                  `items-center` lined up the two boxes, and the pill's box is
                  taller than the words beside it because it holds a 32px avatar —
                  so its text sat below the outer text even though the boxes were
                  centred on each other. `items-baseline` aligns type to type.
                  A flex container takes its baseline from its first
                  *baseline-aligned* item, so the avatar carries `self-center` and
                  is skipped; the name becomes the pill's baseline. Neither the
                  avatar's size nor the title's line height changes. */}
              {/* Headline 17/22 on a phone. */}
              <h2 className="text-base md:text-xl phone:text-[17px] phone:leading-[22px] font-semibold text-center flex flex-wrap items-baseline justify-center gap-1.5 min-w-0" style={{ color: 'var(--editor-text)' }}>
                {titleFirstName ? (
                  <>
                    {/* Dropped on a phone: at 375 the words and the name cannot
                        both fit, and the name is the part that says whose lesson
                        this is. */}
                    <span className="min-w-0 [overflow-wrap:anywhere] phone:hidden">{displayTitle} for</span>
                    <button
                      type="button"
                      ref={playerChipRef}
                      onClick={() => {
                        const r = playerChipRef.current?.getBoundingClientRect();
                        if (r) { setPlayerCardAnchor({ x: r.left + r.width / 2, y: r.bottom + 8 }); setPlayerCardBox({ top: r.top, bottom: r.bottom + 4, left: r.left, right: r.right }); }
                        setPlayerCardOpen((v) => !v);
                      }}
                      aria-haspopup="dialog"
                      aria-expanded={playerCardOpen}
                      data-testid="header-player-name"
                      className="pointer-events-auto flex items-baseline gap-2 flex-shrink-0 phone:flex-shrink phone:min-w-0 rounded-full pr-2.5 pl-1 py-1 -my-1 phone:relative phone:after:absolute phone:after:inset-x-0 phone:after:-inset-y-1 phone:after:content-[''] transition-colors hover:bg-[rgba(255,255,255,0.10)]"
                    >
                      {playerAvatarUrl ? (
                        <img
                          src={playerAvatarUrl}
                          alt=""
                          className="w-7 h-7 md:w-8 md:h-8 rounded-full object-cover flex-shrink-0 self-center"
                        />
                      ) : (
                        <span
                          className="w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 self-center text-xs font-semibold"
                          style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}
                        >
                          {(playerName || titleFirstName).charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="min-w-0 text-left [overflow-wrap:anywhere]">{titleFirstName}</span>
                    </button>
                  </>
                ) : (
                  <span className="min-w-0 [overflow-wrap:anywhere]">{displayTitle}</span>
                )}
              </h2>
              {subtitle && (
                <p className="text-xs text-center [overflow-wrap:anywhere]" style={{ color: 'var(--editor-text-muted)' }}>{subtitle}</p>
              )}
              {/* The save status, under the name on a phone. Beside it in the
                  header row it took the width the name needed. Caption 1 12/16. */}
              {onSaveDraft && (
                <p className={`hidden phone:block text-[12px] leading-[16px] text-center ${saveStatusClass}`}>
                  {phoneSaveLabel}
                </p>
              )}

            </div>

            {/* Auto-save status indicator. "Saved" now means it: a pending edit
                outranks it, so the label cannot reassure a coach who is about to
                close the tab on work that has not been written. */}
            {onSaveDraft && (
              <span className={`text-xs phone:hidden ${saveStatusClass}`}>
                {saveStatusLabel}
              </span>
            )}

            {/* Organizer toggle */}
            <button
              type="button"
              onClick={() => setOrganizerOpen(prev => !prev)}
              className={organizerOpen ? EBTN_ICON_ACTIVE : EBTN_ICON}
              aria-pressed={organizerOpen}
              title={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
              aria-label={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
              </svg>
            </button>

            {/* Submit — three states: idle / submitting / handing off.
                There is no success state here any more, and that is the point.
                A checkmark reading "Sent" used to appear ~1.2s after the click,
                which was ~1.2s after `onSubmitDraft` resolved — and that resolves
                the moment the submission is handed to the background, before a
                single byte has uploaded. It was telling coaches the lesson had
                landed while the 34-second upload had not started. Lesson
                5DXMbk5z5K1YVvArNVVD was lost to exactly that: told "Sent", closed
                the tab, delivery discarded with no server-side trace.
                The button now keeps spinning and says "Sending", and the only
                thing allowed to claim delivery is the indicator, which reads
                lesson.submissionStatus and therefore outlives this tab. */}
            {onSubmitDraft && (
              <button
                data-testid="lve-submit-btn"
                onClick={() => {
                  // Final program lesson: open the progress-report modal
                  // instead of the standard submit confirmation. The modal's
                  // CTA is what eventually fires the actual lesson submission
                  // (paired with the report write).
                  if (isFinalProgramLesson && onFinalSubmit) {
                    setProgressReportOpen(true);
                  } else {
                    setShowSubmitConfirmation(true);
                  }
                }}
                disabled={!hasClips || isSubmitting || submitSuccess}
                /* A full-height filled pill at full opacity, against the recipe's
                   40% disabled. Before this it was a 30px rounded rect at
                   disabled:opacity-50, close enough to its own enabled state on a
                   dark header to read as greyed out when it is live. The success
                   fill is inline so it beats the recipe's background
                   deterministically rather than by emission order. */
                className={EBTN_PRIMARY}
              >
                {submitSuccess ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span className="hidden md:inline">Sending…</span>
                  </>
                ) : isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span className="hidden md:inline">Submitting…</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                    <span className="hidden md:inline">
                      {isFinalProgramLesson && onFinalSubmit ? 'Write Report & Submit Lesson' : 'Submit'}
                    </span>
                  </>
                )}
              </button>
            )}

            <button type="button" onClick={requestClose} className={EBTN_ICON} aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Toolbar ── */}
        {/* <div className="border-b border-db-border bg-db-bg px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            {// Define notable segment //}
            <div className="flex items-center gap-0.5">
              <button
                onClick={handleNotableSegmentBound}
                className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                  notableSegFirstBound !== null
                    ? 'bg-db-accent text-white animate-pulse'
                    : 'text-db-accent hover:text-db-accent hover:bg-db-navy-light'
                }`}
                title={notableSegFirstBound === null
                  ? 'Define notable segment (click to set start)'
                  : 'Click to set end of segment (same point = freeze frame)'}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                </svg>
              </button>
              {notableSegFirstBound !== null && (
                <button
                  onClick={handleCancelNotableSegBound}
                  className="w-8 h-8 flex items-center justify-center rounded text-db-text-3 hover:text-red-500 hover:bg-red-50"
                  title="Cancel segment definition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <path strokeLinecap="round" d="M15 9l-6 6M9 9l6 6"/>
                  </svg>
                </button>
              )}
            </div>

            {// Divider //}
            <div className="w-px h-6 bg-gray-300" />

            {// Segment start indicator //}
            {notableSegFirstBound !== null && (
              <span className="text-xs text-db-accent font-mono">
                Start: {formatTime(notableSegFirstBound)}
              </span>
            )}

            {// Mute button (pushed right) //}
            <div className="flex items-center gap-0.5 ml-auto">
              <button
                onClick={handleToggleAudioMute}
                className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                  audioMuted
                    ? 'text-red-500 hover:text-red-600 hover:bg-red-50'
                    : 'text-db-text-3 hover:text-db-text-2 hover:bg-db-bg'
                }`}
                title={audioMuted ? 'Original audio muted (click to unmute)' : 'Original audio on (click to mute)'}
              >
                {audioMuted ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div> */}

        {/* ── Content: video area (flex-1) + organizer panel (fixed width) ── */}
        <div className={isMobile
          ? (phoneLandscape ? 'relative flex-1 overflow-hidden bg-black' : 'flex flex-col flex-1 overflow-hidden')
          : 'flex flex-col md:flex-row flex-1 overflow-hidden p-0 md:p-3 md:pb-5 gap-0 md:gap-3'}>

          {/* ── Left: video + timeline + toolbar ── */}
          {/* On a phone the column is as tall as the picture plus its controls,
              whatever the plan is doing; the plan takes the rest. The clip
              editor still gets the whole column, because it is drawn inside it. */}
          <div className={isMobile
            ? (phoneLandscape ? 'absolute inset-0 flex flex-col items-center justify-center overflow-hidden' : `flex flex-col overflow-hidden relative ${showClipEditorModal ? 'flex-1 min-w-0' : 'flex-none'}`)
            : `flex flex-col overflow-hidden relative rounded-none md:rounded-xl md:border md:border-[var(--editor-border)] ${organizerOpen ? 'h-[25vh] max-h-[150px] md:h-auto md:max-h-none md:flex-1' : 'flex-1'} ${showClipEditorModal ? '' : 'phone:flex-none phone:h-auto phone:max-h-none'}`} style={{ background: 'var(--editor-bg)' }}>
            {/* Video player — fills all available space, black letterbox.
                On a phone it is the stage: a fixed 16:9 box from the viewport,
                the size it will be from the first frame, with the video drawn
                inside it. */}
            <div
              ref={isMobile ? viewStageRef : undefined}
              data-zoom-surface={isMobile ? '' : undefined}
              onTouchStart={isMobile ? onStageTouchStart : undefined}
              onTouchMove={isMobile ? onStageTouchMove : undefined}
              onTouchEnd={isMobile ? onStageTouchEnd : undefined}
              className={isMobile ? 'relative flex-none self-center bg-black overflow-hidden' : 'flex-1 min-h-0 phone:flex-none bg-black overflow-hidden'}
              style={isMobile ? {
                /* Spec 14.9: not zoomed, a vertical swipe on the video scrolls
                   the page (Safari's bars collapse); pinch and the zoomed pan
                   are usePinchZoom's, which takes the touch only then. */
                touchAction: viewZoom.scale > 1.001 ? 'none' : 'pan-y',
                width: phoneView.stage.w,
                height: phoneView.stage.h,
                /* Spec 11.2: above the organizer's tap-to-close layer (z 30), so
                   pinch and Reset work with a menu open; the recorder (z 40)
                   still covers it. */
                zIndex: 31,
                /* Also eases when the landscape side panel slides. */
                transition: stageSettling || phoneLandscape ? 'width 250ms ease, height 250ms ease' : undefined,
              } : undefined}
            >
              <VideoEditorPlayer
                ref={videoPlayerRef}
                videoUrl={videoUrl}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={setDuration}
                onPlayStateChange={setIsPlaying}
                onTogglePlay={handleTogglePlay}
                audioMuted={audioMuted}
                rotation={videoRotation}
                fillHeight
                onVideoSize={isMobile ? handleVideoSize : undefined}
                className={isMobile ? `transition-opacity duration-300 ${mainVideoSize ? 'opacity-100' : 'opacity-0'}` : ''}
                zoomWrapRef={isMobile ? viewZoomRef : undefined}
                noTapTargets={phoneLandscape}
              />
              {isMobile && <ZoomResetChip scale={viewZoom.scale} onReset={viewZoom.reset} />}
            </div>

            {isMobile ? (phoneLandscape ? null : phoneTransport) : (<>
            {/* Timeline + controls row */}
            {/* On a phone, two rows: the timeline gets the full width, and the
                tools sit under it at 44 targets. Sharing one row at 375 left
                the scrubber about 40px wide. */}
            {/* Landscape with room for it, one row: the timeline beside the
                tools. */}
            <div
              ref={setTransportEl}
              className={isMobile
                ? `flex-shrink-0 flex items-center px-3 py-1 ${phoneView.transport === 96 ? 'flex-wrap gap-x-3 gap-y-0' : 'gap-3'}`
                : 'flex-shrink-0 flex items-center gap-3 px-4 phone:flex-wrap phone:gap-y-0 phone:px-3 phone:py-1'}
              style={{ height: isMobile ? phoneView.transport : '56px', background: 'var(--editor-timeline)', borderTop: '1px solid var(--editor-border)' }}
            >
              <div className={isMobile
                ? `flex-1 min-w-0 h-11 grid content-center lve-phone-timeline ${phoneView.transport === 96 ? 'basis-full' : ''}`
                : 'flex-1 min-w-0 phone:basis-full phone:h-11 phone:grid phone:content-center lve-phone-timeline'}>
                <Timeline
                  duration={duration}
                  currentTime={currentTime}
                  slowMotionSegments={notableSegments}
                  playIntervals={lessonRequest?.playerVideoPlayIntervals}
                  onCreateHighlightFromInterval={handleCreateHighlightFromInterval}
                  isPlaying={isPlaying}
                  onSeek={handleSeek}
                  onScrubStart={beginTransportScrub}
                  onScrubEnd={endTransportScrub}
                  onTogglePlay={handleTogglePlay}
                  onUpdateSloMoSegmentEndTime={handleUpdateNotableSegmentEndTime}
                />
              </div>
              <div className={isMobile
                ? `flex-none flex items-center h-11 ${phoneView.transport === 96 ? 'basis-full justify-between' : 'gap-2'}`
                : 'flex-none flex items-center gap-2 phone:basis-full phone:h-11 phone:justify-between'}>
                {/* ±5s skip buttons */}
                <button
                  onClick={() => handleSeek(Math.max(0, currentTime - 5))}
                  className={EBTN_ICON}
                  title="Rewind 5 seconds"
                  aria-label="Rewind 5 seconds"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                    <text x="7" y="15.5" fontSize="6" fontWeight="bold" fill="currentColor">5</text>
                  </svg>
                </button>
                <button
                  onClick={() => handleSeek(Math.min(duration, currentTime + 5))}
                  className={EBTN_ICON}
                  title="Forward 5 seconds"
                  aria-label="Forward 5 seconds"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
                    <text x="8" y="15.5" fontSize="6" fontWeight="bold" fill="currentColor">5</text>
                  </svg>
                </button>
                <div className="w-px h-6" style={{ background: 'var(--editor-border)' }} />
                {/* Capture the last 15 seconds */}
                <button
                  data-testid="highlight-tool-btn"
                  data-current-time={currentTime}
                  onClick={handleCaptureSegment}
                  className={EBTN_ICON}
                  style={{ color: 'var(--color-biscayne)' }}
                  title="Save the last 15 seconds"
                  aria-label="Save the last 15 seconds"
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                  </svg>
                </button>
                {/* Divider */}
                <div className="w-px h-6" style={{ background: 'var(--editor-border)' }} />
                {/* Audio mute */}
                <button
                  onClick={handleToggleAudioMute}
                  className={EBTN_ICON}
                  /* Inline, per the recipe: a second arbitrary-value text
                     colour class would resolve by emission order, not by
                     class order. */
                  /* Phone: grey when muted, white when not. Red read as an error. */
                  style={isMobile
                    ? { color: audioMuted ? 'var(--editor-text-muted)' : 'var(--editor-text)' }
                    : (audioMuted ? { color: 'var(--editor-danger)' } : undefined)}
                  title={audioMuted ? 'Original audio muted (click to unmute)' : 'Original audio on (click to mute)'}
                  aria-label={audioMuted ? 'Unmute' : 'Mute'}
                >
                  {audioMuted ? (
                    <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  ) : (
                    <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
                {/* Divider */}
                <div className="w-px h-6" style={{ background: 'var(--editor-border)' }} />
                {/* Rotate video clockwise */}
                <button
                  onClick={() => setVideoRotation(prev => ((prev + 90) % 360) as 0 | 90 | 180 | 270)}
                  className={EBTN_ICON}
                  style={videoRotation !== 0 ? { color: 'var(--color-db-oasis)' } : undefined}
                  title={videoRotation !== 0 ? `Video rotated ${videoRotation}° (click to rotate more)` : 'Rotate video 90° clockwise'}
                  aria-label="Rotate video clockwise"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zm-3.04 5.47l1.42 1.42c1.16-1.16 1.71-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.47z"/>
                  </svg>
                </button>
              </div>
            </div>
            </>)}

            {/* ── Embedded Clip Editor ──
                Inset with a scrim behind it, so it reads as one level deeper
                rather than as the same dark surface with a new label. The
                scrim uses bg-black at an opacity, matching every other modal
                in this editor, rather than a token: there is no --editor-*
                value for a scrim and the six existing overlays already set
                this convention. */}
            {showClipEditorModal && clipEditorPrimarySource && (
              /* On a phone the recorder is full screen, inside the insets,
                 rather than inset in this column (spec section 2). */
              <div
                className={isMobile ? 'fixed inset-0 lve-fill z-[40]' : 'absolute inset-0 z-[40] bg-black/60 p-1 md:p-2'}
                /* Landscape (spec 13.15): edge to edge on black; the rails and
                   the timeline keep the safe areas themselves. */
                style={isMobile ? (phoneLandscape ? { background: '#000' } : {
                  background: 'var(--editor-bg)',
                  paddingLeft: 'env(safe-area-inset-left, 0px)',
                  paddingRight: 'env(safe-area-inset-right, 0px)',
                  paddingTop: 'env(safe-area-inset-top, 0px)',
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                }) : undefined}
              >
                <ClipEditorModal
                  embedded
                  isOpen={showClipEditorModal}
                  onClose={() => setShowClipEditorModal(false)}
                  onSave={handleSaveClip}
                  onSaveAndContinue={handleSaveClipAndStay}
                  primarySource={clipEditorPrimarySource}
                  secondarySource={clipEditorSecondarySource ?? undefined}
                  /* Passed unconditionally now. With no second source the
                     control adds one; with one it swaps it, which is the only
                     way a coach could change a comparison at all. */
                  onAddSecondarySource={handleAddSecondarySourceToClipEditor}
                />
              </div>
            )}

          </div>

          {/* Mobile toggle bar */}
          <button
            data-testid="lesson-organizer-toggle"
            onClick={() => setOrganizerOpen(prev => !prev)}
            /* Portrait phones only. In landscape the plan is a rail beside the
               stage and the header button opens and closes it. */
            /* Not on a phone (spec 9.2): the plan is always shown under the
               video in portrait, and the header toggle slides it in landscape. */
            className={isMobile
              ? 'hidden'
              : 'flex md:hidden items-center justify-center gap-2 flex-shrink-0 w-full'}
            /* 44, the minimum hit region. Subhead 15/20, the secondary-control
               role. This bar only exists below md. */
            style={{ height: '44px', background: 'var(--editor-sidebar)', borderTop: '1px solid var(--editor-border)' }}
          >
            <span className="text-[15px] leading-[20px] font-medium" style={{ color: 'var(--editor-text-muted)' }}>Lesson Plan</span>
            <svg
              className="w-4 h-4 transition-transform"
              style={{ color: 'var(--editor-text-muted)', transform: organizerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* ── Right: Lesson Organizer ── */}
          {/* Hidden file input for upload side-by-side */}
          <input
            ref={uploadFileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file || !pendingUploadSegmentIdRef.current) return;
              const segId = pendingUploadSegmentIdRef.current;
              pendingUploadSegmentIdRef.current = null;
              e.target.value = '';
              const primarySeg = notableSegments.find((s) => s.id === segId);
              if (!primarySeg) return;
              const blobUrl = URL.createObjectURL(file);
              setClipEditorPrimarySource({ videoUrl: videoUrl ?? '', segment: primarySeg, label: primarySeg.label, rotation: videoRotation || undefined });
              setClipEditorSecondarySource({ videoUrl: blobUrl, label: file.name.replace(/\.[^.]+$/, '') });
              setShowClipEditorModal(true);
            }}
          />
          {(organizerOpen || isMobile) && (
            /* Phone: one page that scrolls, tabs included (spec 9.2). In
               landscape a side panel that slides with the header toggle: the
               outer box animates its width, the inner keeps the panel's width
               so nothing reflows while it moves. */
            <div
              className={isMobile
                ? (phoneLandscape ? 'absolute z-[33] lve-glass overflow-x-hidden overflow-y-auto overscroll-contain' : 'flex-1 w-full overflow-y-auto min-h-0 overscroll-contain')
                : 'flex-1 w-full md:flex-none md:w-[320px] overflow-y-auto flex-shrink-0 min-h-0'}
              /* Landscape (spec 13.14): a glass panel over the video at the
                 right, between the top and bottom controls, sliding in and
                 out; the video never resizes. */
              style={phoneLandscape ? {
                top: 'calc(var(--lve-edge-top) + 52px)',
                bottom: 'calc(var(--lve-edge-bottom) + 52px)',
                right: 'var(--lve-edge-right)',
                width: landscapeSheetWidth(phoneViewport),
                borderRadius: 20,
                transform: organizerOpen ? 'none' : 'translateX(calc(100% + var(--lve-edge-right)))',
                opacity: organizerOpen ? 1 : 0,
                transition: 'transform 200ms ease, opacity 200ms ease',
                pointerEvents: organizerOpen ? 'auto' : 'none',
              } : undefined}
              onPointerDown={phoneLandscape ? pokeChrome : undefined}
            >
              {(() => {
                const panel = (
                  <LessonOrganizerPanel
                    clips={voiceoverClips}
                    notableSegments={notableSegments}
                    notes={notes}
                    onNotesChange={setNotes}
                    coachNotes={coachNotes}
                    onCoachNotesChange={setCoachNotes}
                    onAddRecording={handleAddRecordingForSegment}
                    onAddSideBySide={handleAddSideBySideForSegment}
                    onDeleteClip={handleDeleteClip}
                    onDeleteSegment={handleDeleteNotableSegment}
                    onPlaySegment={handlePlaySegment}
                    onRenameSegment={(segId, newLabel) => handleUpdateSegment(segId, { label: newLabel })}
                    onReorderSegments={handleReorderSegments}
                    attachedDrills={attachedDrills}
                    onToggleDrill={handleToggleDrill}
                    coachId={currentUser?.uid ?? ''}
                    onPreviewClip={setPreviewClipId}
                    onPreviewLesson={() => { setPreviewLessonIdx(0); setPreviewLessonOpen(true); }}
                    onRenameClip={handleRenameClip}
                    hasPlayerLibrary={!!playerId}
                    offeringType={offeringType}
                  />
                );
                return phoneLandscape ? <div style={{ width: landscapeSheetWidth(phoneViewport) }}>{panel}</div> : panel;
              })()}
            </div>
          )}

          {/* ── Landscape controls over the video (spec 13.14) ── */}
          {phoneLandscape && !showClipEditorModal && (
            <div className={`lve-controls z-[34] ${chromeVisible ? '' : 'lve-controls--hidden'}`} style={{ opacity: chromeVisible ? 1 : 0, transition: 'opacity 200ms ease' }} onPointerDown={pokeChrome}>
              {/* Top: close at the left, the player and the save status in the
                  centre, the plan toggle and Submit at the right. */}
              {/* Spec 14.3: one round button at the top left. A tap expands it
                  to the right into close, the player's avatar and "name ·
                  status"; a tap outside, a tap on it again, or 4s collapses
                  it. A red dot shows on it while the work is not saved. */}
              <div ref={cornerRef} className="lve-glass lve-pill lve-hit absolute left-0 top-0 flex items-center overflow-hidden" style={{ maxWidth: cornerOpen ? 'min(440px, 70vw)' : 44, transition: 'max-width 200ms ease' }}>
                <button
                  type="button"
                  onClick={() => setCornerOpen((o) => !o)}
                  aria-expanded={cornerOpen}
                  aria-label={cornerOpen ? 'Hide lesson details' : 'Show lesson details'}
                  className="relative flex-none w-11 h-11 flex items-center justify-center ios-press"
                  data-testid="landscape-corner-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                  {!cornerOpen && (saveStatus === 'error' || saveStatus === 'retrying') && (
                    <span className="absolute top-2 right-2 w-2 h-2 rounded-full" style={{ background: 'var(--editor-danger)' }} aria-label="Not saved" />
                  )}
                </button>
                <button type="button" onClick={requestClose} tabIndex={cornerOpen ? 0 : -1} className="flex-none w-11 h-11 flex items-center justify-center ios-press" aria-label="Close" title="Close">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                <button
                  type="button"
                  ref={playerChipRef}
                  tabIndex={cornerOpen ? 0 : -1}
                  onClick={() => {
                    if (!titleFirstName) return;
                    const r = playerChipRef.current?.getBoundingClientRect();
                    if (r) { setPlayerCardAnchor({ x: r.left + r.width / 2, y: r.bottom + 8 }); setPlayerCardBox({ top: r.top, bottom: r.bottom + 4, left: r.left, right: r.right }); }
                    setPlayerCardOpen((v) => !v);
                  }}
                  aria-haspopup="dialog"
                  aria-expanded={playerCardOpen}
                  data-testid="header-player-name"
                  className="flex-none min-w-0 flex items-center gap-2 pr-3.5 h-11 ios-press"
                >
                  {titleFirstName && (playerAvatarUrl ? (
                    <img src={playerAvatarUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.18)' }}>
                      {(playerName || titleFirstName).charAt(0).toUpperCase()}
                    </span>
                  ))}
                  {/* Subhead 15/20: the name, then the save status in grey; a long name wraps. */}
                  <span className="flex flex-wrap items-baseline min-w-0 text-[15px] leading-[20px]">
                    <span className="font-semibold min-w-0 [overflow-wrap:anywhere]">{titleFirstName || displayTitle}</span>
                    {onSaveDraft && (
                      <span className={`flex-none ${saveStatus === 'error' || saveStatus === 'retrying' ? 'text-[var(--editor-danger)]' : 'text-white/70'}`} data-testid="phone-save-status">
                        {'\u00a0· '}{!draftReady ? 'Loading' : phoneSaveLabel}
                      </span>
                    )}
                  </span>
                </button>
              </div>
              <div className="absolute right-0 top-0 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOrganizerOpen((prev) => !prev)}
                  aria-pressed={organizerOpen}
                  title={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
                  aria-label={organizerOpen ? 'Hide lesson plan' : 'Show lesson plan'}
                  className="lve-glass lve-pill lve-hit w-11 flex items-center justify-center ios-press"
                  style={organizerOpen ? { background: 'rgba(255,255,255,0.22)' } : undefined}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" /></svg>
                </button>
                {onSubmitDraft && (
                  <button
                    data-testid="lve-submit-btn"
                    onClick={() => {
                      if (isFinalProgramLesson && onFinalSubmit) setProgressReportOpen(true);
                      else setShowSubmitConfirmation(true);
                    }}
                    disabled={!hasClips || isSubmitting || submitSuccess}
                    aria-busy={isSubmitting || submitSuccess}
                    className={`${EBTN_PRIMARY} lve-hit`}
                    /* Keeps its teal (biscayne). 44, a pill, Subhead 15/20. */
                    style={{ height: 44, borderRadius: 9999, fontSize: 15, lineHeight: '20px', paddingLeft: 16, paddingRight: 16 }}
                  >
                    {isSubmitting || submitSuccess
                      ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-label="Sending" />
                      : 'Submit'}
                  </button>
                )}
              </div>
              {/* Centre play while paused. */}
              {!isPlaying && (
                <button type="button" onClick={handleTogglePlay} className="lve-glass lve-hit absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center ios-press" aria-label="Play">
                  <svg className="w-8 h-8 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </button>
              )}
              {/* Bottom: the controls pill. */}
              <div className="absolute left-0 right-0 bottom-0">{phoneTransport}</div>
            </div>
          )}
        </div>

      </div>

      {/* Spec 14.10: the one-time hint, top centre, Subhead 15/20. */}
      {barsHint !== 'off' && (
        <div
          role="status"
          className="lve-glass lve-pill fixed left-1/2 -translate-x-1/2 w-max max-w-[calc(100vw-32px)] px-4 flex items-center text-center text-[15px] leading-[20px] font-semibold pointer-events-none"
          style={{ top: 'var(--lve-edge-top)', zIndex: 'var(--z-editor-menu)', opacity: barsHint === 'on' ? 1 : 0, transition: 'opacity 300ms ease' }}
        >
          Swipe up for full screen
        </div>
      )}

      {/* ?editorDebug=1: the phone decision, its inputs and the build, on screen. */}
      <EditorDebugOverlay videos={debugVideos} detection />

      {/* ── Segment naming modal ── */}
      {/* ── Player details ──
          A child of the editor root, not of the header.
          It used to sit inside the title container, which carries
          -translate-x-1/2. A transform creates a stacking context, so the
          popover's z-index was scoped inside it and could not rise above the
          video pane: the pane and the header are both positioned at z-auto in
          the root's context, and the pane comes later in the DOM, so it painted
          on top. Moving the popover here puts it after the pane in DOM order
          inside the same z-[9999] root, which is the fix; the z-index below only
          settles it against its immediate siblings.

          Deliberately NOT portalled to document.body. Four portals in this
          editor already render behind the root and are invisible. */}
      {playerCardOpen && playerCardAnchor && (
        <div
          ref={(el) => {
            playerCardRef.current = el;
            if (isMobile) placeOnMount(playerCardBox, phoneViewport.insets)(el);
          }}
          role="dialog"
          aria-label={`${playerName || titleFirstName} details`}
          data-testid="header-player-card"
          className={`absolute z-50 w-60 ${isMobile ? '' : '-translate-x-1/2'} rounded-xl p-4 shadow-2xl text-left`}
          style={{
            left: playerCardAnchor.x,
            top: playerCardAnchor.y,
            background: 'var(--editor-sidebar)',
            border: '1px solid var(--editor-border)',
          }}
        >
          <div className="flex items-center gap-3">
            {playerAvatarUrl ? (
              <img src={playerAvatarUrl} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold"
                style={{ background: 'var(--editor-surface)', color: 'var(--editor-text-muted)' }}
              >
                {(playerName || titleFirstName || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold [overflow-wrap:anywhere]" style={{ color: 'var(--editor-text)' }}>
                {playerName || titleFirstName}
              </div>
              {playerUtr != null && (
                <div className="text-xs" style={{ color: 'var(--editor-text-muted)' }}>UTR {playerUtr}</div>
              )}
            </div>
          </div>
          {playerGoal && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--editor-border)' }}>
              <div className="text-[11px]" style={{ color: 'var(--editor-text-muted)' }}>Goal</div>
              <div className="mt-0.5 text-xs" style={{ color: 'var(--editor-text)' }}>{playerGoal}</div>
            </div>
          )}
          {playerUtr == null && !playerGoal && (
            <div className="mt-3 text-xs" style={{ color: 'var(--editor-text-muted)' }}>
              No UTR or goal on file.
            </div>
          )}
        </div>
      )}

      {/* ── Segment capture confirmation ── */}
      {pendingSegmentBounds && (
        <SegmentCaptureModal
          captureStart={pendingSegmentBounds.startTime}
          captureEnd={pendingSegmentBounds.endTime}
          duration={duration}
          defaultName={segmentNameInput}
          onSeek={(t) => videoPlayerRef.current?.seek(t)}
          onSave={handleSaveCapture}
          onCancel={handleCancelCapture}
          position={capturePanelPos}
          onPositionChange={setCapturePanelPos}
          organizerOpen={organizerOpen}
          transportEl={transportEl}
          stageEl={isMobile ? viewStageRef.current : null}
          phoneDock={segmentSheetDock}
        />
      )}

      {/* ── Notable segment delete confirmation ── */}
      {showNotableSegDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>
            <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>Delete highlight?</h3>
            <p className="mb-6 text-sm" style={{ color: 'var(--editor-text-muted)' }}>
              This segment is referenced by {showNotableSegDeleteConfirm.clipCount} recording{showNotableSegDeleteConfirm.clipCount > 1 ? 's' : ''}. Deleting it will also delete those recordings.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNotableSegDeleteConfirm(null)}
                className={EBTN_SECONDARY}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteNotableSegment}
                className={EBTN_DESTRUCTIVE}
              >
                Delete Highlight &amp; Recordings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add comparison source modal ── */}
      {showComparisonSourceModal && clipEditorPrimarySource && (
        <AddComparisonSourceModal
          isOpen={showComparisonSourceModal}
          defaultTab={comparisonDefaultTab}
          onClose={() => {
            setShowComparisonSourceModal(false);
            setComparisonDefaultTab(undefined);
            openClipEditorAfterComparisonRef.current = false;
          }}
          onConfirm={(source) => {
            setClipEditorSecondarySource(source);
            setShowComparisonSourceModal(false);
            setComparisonDefaultTab(undefined);
            if (openClipEditorAfterComparisonRef.current) {
              openClipEditorAfterComparisonRef.current = false;
              setShowClipEditorModal(true);
            }
          }}
          primarySource={clipEditorPrimarySource}
          videoUrl={videoUrl}
          notableSegments={notableSegments}
          playerId={playerId}
          originalVideoId={originalVideoId}
          videoRotation={videoRotation || undefined}
        />
      )}

      {/* ── Submit confirmation modal ── */}
      {showSubmitConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>
            {isSubmitting || submitSuccess ? (
              <div className="flex flex-col items-center text-center py-2">
                <div
                  className="animate-spin rounded-full h-8 w-8 mb-4"
                  style={{ borderBottom: '2px solid var(--editor-text)' }}
                />
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>
                  Sending your lesson
                </h3>
              </div>
            ) : (
            <>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>Submit Lesson?</h3>
            {/* Not "view your lesson recordings immediately after submission",
                for two reasons worth keeping written down.
                "Immediately" is a promise the processing pipeline cannot always
                keep — Cloud Run transcodes the composition after the submit
                returns, and a long breakdown is minutes behind the click.
                "Recordings" is our internal word for the clips inside the
                editor; the player is shown "breakdown" on every screen they
                see, so a coach reading this and a player reading their lesson
                were being given two names for one thing.
                What replaced it is the fact that matters at this button: it is
                one-way. */}
            <p className="mb-6" style={{ color: 'var(--editor-text-muted)' }}>
              This sends your breakdown to {titleFirstName || 'the player'}. You won&apos;t be able to change it afterwards.
            </p>
            {/* Phone: the two buttons share the width at 48 (spec 6). */}
            <div className="flex gap-3 justify-end phone:justify-stretch">
              <button
                onClick={() => setShowSubmitConfirmation(false)}
                disabled={isSubmitting}
                className={`${EBTN_SECONDARY} phone:flex-1 phone:h-12`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!onSubmitDraft) return;
                  setIsSubmitting(true);
                  /* The dialog deliberately stays open. It swaps to the handoff
                     state below rather than vanishing, because this is the one
                     moment the coach is certainly looking at the screen and the
                     one sentence that matters — keep this tab open — has to be
                     somewhere they will read it. */
                  try {
                    await onSubmitDraft(buildDraftData());
                    setIsSubmitting(false);
                    setSubmitSuccess(true);
                    /* 600ms, down from 1200. It was sized to let a coach read
                       "Sent"; there is nothing to read now, and the handoff copy
                       lives in the dialog rather than this flash. */
                    setTimeout(() => {
                      setShowSubmitConfirmation(false);
                      onSubmitSuccess?.(lessonRequest?.id ?? lessonId ?? '');
                      setSubmitSuccess(false);
                    }, 600);
                  } catch (error) {
                    showError('Failed to submit lesson');
                    console.error('Submit draft error:', error);
                    setIsSubmitting(false);
                    // The dialog no longer closes on confirm, so close it here.
                    setShowSubmitConfirmation(false);
                  }
                }}
                disabled={isSubmitting}
                className={`${EBTN_PRIMARY} phone:flex-1 phone:h-12`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Submitting...
                  </>
                ) : (
                  'Submit Lesson'
                )}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}
      {/* ── Clip preview modal ── */}
      {previewClipId && (
        <div
          className={isMobile ? 'fixed inset-0 lve-fill z-[70] flex flex-col' : 'fixed inset-0 z-[70] flex items-center justify-center bg-black/80'}
          style={isMobile ? phoneSheetStyle : undefined}
          onClick={() => setPreviewClipId(null)}
        >
          <div
            className={isMobile ? 'flex flex-col w-full h-full overflow-hidden' : 'rounded-xl overflow-hidden w-full max-w-2xl mx-4 shadow-2xl'}
            style={isMobile ? undefined : { background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={isMobile ? 'flex items-center justify-between px-3 flex-shrink-0' : 'flex items-center justify-between px-4 py-3'}
              style={isMobile ? { height: PHONE_HEADER, borderBottom: '1px solid var(--editor-border)' } : { borderBottom: '1px solid var(--editor-border)' }}
            >
              {/* Phone (spec 15): titles are Subhead 15/20 semibold, at most 120 wide;
                  a longer one wraps to two lines and steps down, never cut. */}
              <FitText as="h3" lines={2} className="text-sm font-semibold text-[var(--editor-text)] phone:text-[15px] phone:leading-[20px] phone:max-w-[120px]">
                {voiceoverClips.find((c) => c.id === previewClipId)?.label ?? 'Clip Preview'}
              </FitText>
              <button onClick={() => setPreviewClipId(null)} className="phone:relative text-[var(--editor-text-muted)] hover:text-[var(--editor-text)] phone:after:absolute phone:after:-inset-3 phone:after:content-['']" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div
              className={isMobile ? 'bg-black flex-none self-center' : 'bg-black'}
              style={isMobile ? { width: clipPreviewStage.w, height: clipPreviewStage.h } : { minHeight: 200 }}
            >
              {previewClipLoading ? (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-db-oasis)]"/></div>
              ) : previewClipUrl ? (
                <EditorVideo
                  src={previewClipUrl}
                  controls
                  autoPlay
                  playsInline
                  className={isMobile ? 'w-full h-full object-contain' : 'w-full max-h-[60vh] phone:max-h-[60dvh] object-contain'}
                  wrapperClassName="w-full h-full"
                  onLoadedMetadata={isMobile ? handlePreviewMetadata : undefined}
                />
              ) : (
                <div className="flex items-center justify-center py-16 text-sm text-[var(--editor-text-muted)]">Preview not available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {leaveWhileUploading && (
        <RecorderAlert
          title="Clip still uploading"
          actions={[
            { label: 'Wait', role: 'cancel', onPress: () => setLeaveWhileUploading(false) },
            { label: 'Leave Anyway', role: 'destructive', onPress: () => { setLeaveWhileUploading(false); void handleClose(); } },
          ]}
        />
      )}

      {/* ── Lesson preview modal ── */}
      {previewLessonOpen && (
        <div
          className={isMobile ? 'fixed inset-0 lve-fill z-[70] flex flex-col' : 'fixed inset-0 z-[70] flex items-center justify-center bg-black/80'}
          /* Landscape (spec 13.16): edge to edge on black; the floating
             controls keep the safe areas (13.13). */
          style={isMobile ? (phoneLandscape ? { background: '#000' } : phoneSheetStyle) : undefined}
          onClick={() => setPreviewLessonOpen(false)}
        >
          <div
            className={isMobile ? 'flex flex-col w-full h-full overflow-hidden' : 'rounded-xl overflow-hidden w-full max-w-2xl mx-4 shadow-2xl flex flex-col'}
            style={isMobile ? undefined : { maxHeight: '85vh', background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={isMobile ? 'flex items-center justify-between px-3 flex-shrink-0' : 'flex items-center justify-between px-4 py-3 flex-shrink-0'}
              style={isMobile ? { height: PHONE_HEADER, borderBottom: '1px solid var(--editor-border)', display: phoneLandscape ? 'none' : undefined } : { borderBottom: '1px solid var(--editor-border)' }}
            >
              <div>
                <h3 className="text-sm font-semibold text-[var(--editor-text)] phone:text-[15px] phone:leading-[20px]">Lesson Preview</h3>
                {/* Phone (spec 15.30): the same line as the desktop. */}
                {previewableClips.length > 0 && (
                  <p className="text-xs text-[var(--editor-text-muted)] mt-0.5 phone:text-[13px] phone:leading-[18px] [overflow-wrap:anywhere]">
                    Clip {previewLessonIdx + 1} of {previewableClips.length}
                    {previewableClips[previewLessonIdx]?.label ? ` · ${previewableClips[previewLessonIdx].label}` : ''}
                  </p>
                )}
              </div>
              <button onClick={() => setPreviewLessonOpen(false)} className="phone:relative text-[var(--editor-text-muted)] hover:text-[var(--editor-text)] phone:after:absolute phone:after:-inset-3 phone:after:content-['']" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            {phonePreviewBody && previewableClips.length > 0 ? (
              <PhoneLessonPreviewBody
                clips={previewableClips}
                allClips={voiceoverClips}
                segments={notableSegments}
                index={previewLessonIdx}
                onIndex={setPreviewLessonIdx}
                url={previewLessonUrl}
                viewport={phoneViewport}
                onClose={() => setPreviewLessonOpen(false)}
              />
            ) : (
            <>
            <div
              className={isMobile ? 'bg-black flex-none self-center' : 'bg-black flex-1 min-h-0'}
              style={isMobile ? { width: lessonPreviewStage.w, height: lessonPreviewStage.h } : undefined}
            >
              {previewableClips.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-sm text-[var(--editor-text-muted)]">No recorded clips to preview</div>
              ) : previewLessonUrl ? (
                <EditorVideo
                  key={previewLessonUrl}
                  src={previewLessonUrl}
                  controls
                  autoPlay
                  playsInline
                  className={isMobile ? 'w-full h-full object-contain' : 'w-full max-h-[55vh] phone:max-h-[55dvh] object-contain'}
                  wrapperClassName="w-full h-full"
                  onLoadedMetadata={isMobile ? handlePreviewMetadata : undefined}
                  onEnded={() => {
                    if (previewLessonIdx < previewableClips.length - 1) setPreviewLessonIdx((i) => i + 1);
                  }}
                />
              ) : (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-db-oasis)]"/></div>
              )}
            </div>
            {/* Phone: the space under the stage is open, and prev/next sits on
                the bottom edge. */}
            {isMobile && <div className="flex-1 min-h-0" />}
            </>
            )}
            {/* Landscape (spec 13.16): Previous and Next are in the bottom pill instead. */}
            {previewableClips.length > 1 && !(isMobile && phoneLandscape) && (
              <div
                className={isMobile ? 'flex items-center justify-between px-3 flex-shrink-0' : 'flex items-center justify-between px-4 py-3 flex-shrink-0'}
                style={isMobile ? { height: 52, borderTop: '1px solid var(--editor-border)' } : { borderTop: '1px solid var(--editor-border)' }}
              >
                <button
                  onClick={() => setPreviewLessonIdx((i) => Math.max(0, i - 1))}
                  disabled={previewLessonIdx === 0}
                  className="px-3 py-1.5 rounded-lg text-sm disabled:opacity-40 text-[var(--editor-text)] hover:bg-[var(--editor-hover)] disabled:cursor-not-allowed flex items-center gap-1.5 phone:min-h-[44px] phone:rounded-full phone:text-[15px] phone:leading-[20px]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
                  Previous
                </button>
                <div className="flex gap-1.5">
                  {previewableClips.map((_, i) => (
                    <button key={i} onClick={() => setPreviewLessonIdx(i)} className={`w-2 h-2 rounded-full transition-colors ${i === previewLessonIdx ? 'bg-[var(--color-db-oasis)]' : 'bg-[var(--editor-border)] hover:bg-[var(--editor-hover-strong)]'}`} aria-label={`Clip ${i + 1}`} />
                  ))}
                </div>
                <button
                  onClick={() => setPreviewLessonIdx((i) => Math.min(previewableClips.length - 1, i + 1))}
                  disabled={previewLessonIdx === previewableClips.length - 1}
                  className="px-3 py-1.5 rounded-lg text-sm disabled:opacity-40 text-[var(--editor-text)] hover:bg-[var(--editor-hover)] disabled:cursor-not-allowed flex items-center gap-1.5 phone:min-h-[44px] phone:rounded-full phone:text-[15px] phone:leading-[20px]"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Final-lesson progress report modal ──
          Opened when the coach hits "Write Report & Submit Lesson" on the
          final program lesson. Controlled component — draft state lives in
          this editor so closing/reopening (X with confirm, Esc with confirm,
          or Save draft) preserves the answers across the editor session. */}
      {progressReportOpen && isFinalProgramLesson && onFinalSubmit && (
        <ProgressReportModal
          playerFirstName={playerFirstName?.trim() || 'player'}
          totalLessons={totalLessons ?? 4}
          playerGoal={playerGoal}
          draft={progressReportDraft}
          onDraftChange={setProgressReportDraft}
          onClose={() => setProgressReportOpen(false)}
          onSubmit={async () => {
            // Lifecycle: modal stays mounted with its own "Submitting…"
            // spinner until this resolves. Editor's outer isSubmitting flag
            // is also flipped so the header Submit button reflects state.
            setIsSubmitting(true);
            try {
              await onFinalSubmit({
                draftData: buildDraftData(),
                report: {
                  whatImproved: progressReportDraft.whatImproved.trim(),
                  keepDoing: progressReportDraft.keepDoing.trim(),
                  nextFocus: progressReportDraft.nextFocus.trim(),
                  personalNote: progressReportDraft.personalNote.trim(),
                },
              });
              // Resolve = lesson submit succeeded (parent owns toast/navigation).
              // Close the modal and clear the draft.
              setProgressReportDraft({ whatImproved: '', keepDoing: '', nextFocus: '', personalNote: '' });
              setProgressReportOpen(false);
              setSubmitSuccess(true);
              setTimeout(() => onSubmitSuccess?.(lessonRequest?.id ?? '', playerFirstName), 1200);
            } catch (err) {
              // Lesson submit failed — modal stays open so the coach can retry.
              setIsSubmitting(false);
              throw err;
            }
          }}
        />
      )}
      </div>
    </div>
    </>,
    document.body,
  );
}
